import { useState, useEffect, createContext, useContext, useRef } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AdminPanel from "./AdminPanel";
import StackCheckout from "./StackCheckout";
import LibraryPage from "./LibraryPage";
import hacksterH from "./assets/hackster-h.png";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize user from localStorage on first render
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(localStorage.getItem('token'));

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          // Token expired
          logout();
        } else {
          // Token valid - load user from localStorage if not already set
          if (!user) {
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            if (userData.email) {
              setUser(userData);
            }
          }
        }
      } catch {
        // Invalid token
        logout();
      }
    }
  }, [token]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user,
    isCoach: user?.role === 'coach'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Navigation Component
const LogoMark = ({ size = 44, dark = false, className = "" }) => (
  <img
    src={hacksterH}
    alt="HACKSTER.ai"
    width={size}
    height={size}
    className={className}
    style={{ objectFit: "contain", height: size, width: "auto" }}
  />
);

const Navigation = () => {
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  
  return (
    <nav className="flex items-center justify-between p-6 max-w-7xl mx-auto">
      <Link to="/" className="flex items-center space-x-3">
        <div className="flex items-center space-x-2.5">
          <LogoMark size={48} className="shrink-0" />
          <div className="flex flex-col">
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-extrabold tracking-tight text-[#1E2A4A]">HACKSTER<span className="text-blue-600">.ai</span></span>
              <span className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide">BETA</span>
            </div>
            <span className="text-xs text-gray-500 -mt-0.5">Your biohacking buddy</span>
          </div>
        </div>
      </Link>
      <div className="hidden md:flex items-center space-x-8">
        <Link to="/marketplace" className={`transition-colors ${location.pathname === '/marketplace' ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'}`}>
          Marketplace
        </Link>
        <Link to="/community" className={`transition-colors ${location.pathname === '/community' ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'}`}>
          Community
        </Link>
        <Link to="/library" className={`transition-colors ${location.pathname === '/library' ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'}`}>
          Library
        </Link>
        <Link to="/questionnaire" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity">
          Get Started
        </Link>
        
        {isAuthenticated ? (
          <div className="flex items-center space-x-4">
            {user?.role === 'admin' && (
              <Link to="/admin" className={`transition-colors ${location.pathname === '/admin' ? 'text-blue-600 font-semibold' : 'text-gray-700 hover:text-blue-600'}`}>
                Admin
              </Link>
            )}
            <Link to={user?.role === 'coach' ? '/coach/dashboard' : '/dashboard'} className="flex items-center space-x-2 text-gray-700 hover:text-blue-600 transition-colors">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">{user?.username?.[0]?.toUpperCase() || 'H'}</span>
              </div>
              <span className="text-sm font-medium">
                {user?.username}
                {user?.role === 'coach' && (
                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                    Coach
                  </span>
                )}
              </span>
            </Link>
            <button
              onClick={logout}
              className="text-gray-500 hover:text-red-600 transition-colors text-sm"
            >
              Logout
            </button>
          </div>
        ) : (
          <Link to="/login" className="text-gray-700 hover:text-blue-600 transition-colors">
            Login
          </Link>
        )}
      </div>
    </nav>
  );
};

// Hero Section Component
const HeroSection = () => {
  return (
    <div className="bg-gradient-to-br from-blue-50 to-purple-50">
      <Navigation />

      {/* Hero Content */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight">
            Optimize Your Health with
            <span className="text-blue-600 block">Biohacking Excellence</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-6 leading-relaxed">
            Take a 2-minute assessment and get an AI-personalized Hackster Stack —
            supplements, devices, and a matched health coach for your top goals.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8 text-sm text-gray-700">
            <span className="bg-white px-3 py-1 rounded-full shadow-sm border border-gray-200">⚡ Increase Energy</span>
            <span className="bg-white px-3 py-1 rounded-full shadow-sm border border-gray-200">🧬 Vitality & Longevity</span>
            <span className="bg-white px-3 py-1 rounded-full shadow-sm border border-gray-200">🛡️ Immune Support</span>
            <span className="bg-white px-3 py-1 rounded-full shadow-sm border border-gray-200">⚖️ Weight Loss</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/questionnaire" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:opacity-90 transition-all shadow-lg hover:shadow-xl text-center">
              Take the Free Assessment →
            </Link>
            <Link to="/marketplace" className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-blue-600 hover:text-white transition-all text-center">
              Shop Marketplace
            </Link>
          </div>
          <p className="text-xs text-gray-500 mt-6">
            Featuring products from <span className="font-semibold">Thorne</span>, <span className="font-semibold">Apex Energetics</span>, <span className="font-semibold">Standard Process</span>, <span className="font-semibold">Bio-Well</span>, <span className="font-semibold">CuraWaves</span> & <span className="font-semibold">StemRegen</span>
          </p>
        </div>
      </div>
    </div>
  );
};

// Core Features Section
const TileIcon = ({ type }) => {
  const common = {
    width: 30, height: 30, viewBox: "0 0 24 24", fill: "none",
    stroke: "white", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round",
  };
  switch (type) {
    case "vision": // target / clear vision
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="4.5" />
          <circle cx="12" cy="12" r="1" fill="white" stroke="none" />
        </svg>
      );
    case "stack": // stacked layers
      return (
        <svg {...common}>
          <path d="M12 2 2 7l10 5 10-5-10-5Z" />
          <path d="M2 12l10 5 10-5" />
          <path d="M2 17l10 5 10-5" />
        </svg>
      );
    case "shop": // shopping bag
      return (
        <svg {...common}>
          <path d="M6 2 3 6v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
          <path d="M3 6h18" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
      );
    case "trophy": // trophy / celebrate
      return (
        <svg {...common}>
          <path d="M8 21h8" />
          <path d="M12 17v4" />
          <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
          <path d="M7 6H4v2a3 3 0 0 0 3 3" />
          <path d="M17 6h3v2a3 3 0 0 1-3 3" />
        </svg>
      );
    default:
      return null;
  }
};

const CoreFeatures = () => {
  const features = [
    {
      id: "questionnaire",
      title: "Establish Your Vision",
      subtitle: "Personalized Recommendations",
      description: "Take our AI-powered questionnaire for personalized supplements, relevant lab tests, and biohacking guidance.",
      iconType: "vision",
      highlights: [],
      cta: "Start AI Assessment",
      color: "blue",
      link: "/questionnaire"
    },
    {
      id: "stack",
      title: "Build Your Stack",
      subtitle: "Save, Share & Crowdsource",
      description: "Create your personalized Hackster Stack, save products, and rally friends and family to help you reach your goals faster.",
      iconType: "stack",
      highlights: [],
      cta: "Create My Stack",
      color: "purple",
      link: "/my-stack"
    },
    {
      id: "marketplace",
      title: "Shop Trusted Vendors",
      subtitle: "Premium Biohacking Products", 
      description: "Browse recommended products from our carefully curated, top brand partners — all in one place.",
      iconType: "shop",
      highlights: [],
      cta: "Visit Hackster Store",
      color: "emerald",
      link: "/marketplace"
    },
    {
      id: "celebrate",
      title: "Celebrate Your Wins",
      subtitle: "Inspire the Community",
      description: "Share your biohacking successes with the Hackster community and help others thrive, too.",
      iconType: "trophy",
      highlights: [],
      cta: "Share Success",
      color: "amber",
      link: "/community"
    }
  ];

  return (
    <div className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Your Path to Optimal Health
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Follow our dynamic, precision biohacking methods to transform your life, 
            increase vitality, and feel better, longer!
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => {
            // Define color classes explicitly to ensure Tailwind includes them
            const getColorClasses = (color) => {
              switch(color) {
                case 'blue':
                  return {
                    background: 'bg-gradient-to-br from-blue-50 to-blue-100',
                    border: 'border-blue-200',
                    text: 'text-blue-600',
                    button: 'bg-blue-600 hover:bg-blue-700',
                    dot: 'bg-blue-500',
                    badge: 'from-blue-500 to-indigo-500'
                  };
                case 'purple':
                  return {
                    background: 'bg-gradient-to-br from-purple-50 to-purple-100',
                    border: 'border-purple-200',
                    text: 'text-purple-600',
                    button: 'bg-purple-600 hover:bg-purple-700',
                    dot: 'bg-purple-500',
                    badge: 'from-purple-500 to-fuchsia-500'
                  };
                case 'emerald':
                  return {
                    background: 'bg-gradient-to-br from-emerald-50 to-teal-100',
                    border: 'border-emerald-200',
                    text: 'text-emerald-600',
                    button: 'bg-emerald-600 hover:bg-emerald-700',
                    dot: 'bg-emerald-500',
                    badge: 'from-emerald-500 to-teal-500'
                  };
                case 'amber':
                  return {
                    background: 'bg-gradient-to-br from-amber-50 to-orange-100',
                    border: 'border-amber-200',
                    text: 'text-amber-600',
                    button: 'bg-amber-500 hover:bg-amber-600',
                    dot: 'bg-amber-500',
                    badge: 'from-amber-400 to-orange-500'
                  };
                default:
                  return {
                    background: 'bg-gradient-to-br from-gray-50 to-gray-100',
                    border: 'border-gray-200',
                    text: 'text-gray-600',
                    button: 'bg-gray-600 hover:bg-gray-700',
                    dot: 'bg-gray-500',
                    badge: 'from-gray-500 to-gray-600'
                  };
              }
            };
            
            const colorClasses = getColorClasses(feature.color);
            
            return (
              <div key={feature.id} className="group hover:scale-105 transition-all duration-300">
                <div className={`${colorClasses.background} rounded-2xl p-8 h-full border ${colorClasses.border} shadow-lg hover:shadow-xl transition-all`}>
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${colorClasses.badge} flex items-center justify-center shadow-md`}>
                      <TileIcon type={feature.iconType} />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className={`${colorClasses.text} font-semibold mb-4`}>{feature.subtitle}</p>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </div>

                  {feature.highlights.length > 0 && (
                    <div className="space-y-3 mb-8">
                      {feature.highlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className={`w-2 h-2 ${colorClasses.dot} rounded-full`}></div>
                          <span className="text-gray-700">{highlight}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className={feature.highlights.length === 0 ? "mt-8" : ""}>
                    <Link 
                      to={feature.link || "#"}
                      className={`w-full ${colorClasses.button} text-white py-3 rounded-xl font-semibold transition-colors shadow-md hover:shadow-lg block text-center`}
                    >
                      {feature.cta}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Priority Supplements Section
const PrioritySupplements = () => {
  const supplements = [
    { name: "Vitamin D3", priority: 1, description: "Essential for immune function and bone health", brands: ["Thorne", "Standard Process"] },
    { name: "Vitamin A", priority: 2, description: "Critical for vision and immune system", brands: ["Thorne", "Apex Energetics"] },
    { name: "Vitamin K2", priority: 3, description: "Bone health and cardiovascular support", brands: ["Thorne", "Standard Process"] },
    { name: "Magnesium", priority: 4, description: "300+ enzymatic reactions, sleep, and recovery", brands: ["Thorne", "Apex Energetics"] },
    { name: "Amino Acids", priority: 5, description: "Building blocks for muscle and neurotransmitters", brands: ["Thorne", "Standard Process"] },
    { name: "Multivitamin", priority: 6, description: "Foundation for nutritional gaps", brands: ["Thorne", "Apex Energetics"] }
  ];

  return (
    <div className="py-14 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Supplement Stacks
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Learn science-backed supplement recommendations featuring premium brands like Thorne, 
            Apex Energetics and Standard Process for foundational health/wellness.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {supplements.map((supplement) => (
            <div key={supplement.name} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-sm">#{supplement.priority}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{supplement.name}</h3>
                </div>
              </div>
              <p className="text-gray-600 mb-4">{supplement.description}</p>
              <div className="flex flex-wrap gap-2">
                {supplement.brands.map((brand) => (
                  <span key={brand} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                    {brand}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Community Platform Component
const CommunityPlatform = () => {
  const { isAuthenticated, user, token } = useAuth();
  const [activeView, setActiveView] = useState('forum'); // Start with forum view
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState({ 
    title: '', 
    content: '', 
    category: 'general',
    image_url: '',
    youtube_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [showNewPostForm, setShowNewPostForm] = useState(false);

  // Fetch posts from backend
  useEffect(() => {
    if (activeView === 'forum') {
      fetchPosts();
    }
  }, [activeView]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserLevel = (level) => {
    switch(level) {
      case 'hackster_pro': return { icon: '🟡', text: 'Hackster Pro', color: 'text-yellow-600 bg-yellow-100' };
      case 'contributor': return { icon: '🔵', text: 'Contributor', color: 'text-blue-600 bg-blue-100' };
      case 'member': return { icon: '🟢', text: 'Member', color: 'text-green-600 bg-green-100' };
      default: return { icon: '🟢', text: 'Member', color: 'text-green-600 bg-green-100' };
    }
  };

  const createPost = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      alert('Please log in to create posts');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/posts`, newPost, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setPosts([response.data, ...posts]);
      setNewPost({ title: '', content: '', category: 'general', image_url: '', youtube_url: '' });
      setShowNewPostForm(false);
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Error creating post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReaction = async (postId, reactionType) => {
    if (!isAuthenticated) {
      alert('Please log in to react to posts');
      return;
    }

    try {
      await axios.post(`${API}/reactions`, {
        post_id: postId,
        reaction_type: reactionType
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh posts to get updated reaction counts
      fetchPosts();
    } catch (error) {
      console.error('Error reacting to post:', error);
    }
  };

  if (activeView === 'forum') {
    return (
      <div className="py-14 bg-gradient-to-br from-blue-50 to-purple-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-8">
            <button 
              onClick={() => setActiveView('overview')}
              className="text-blue-600 hover:text-blue-700 mb-4 flex items-center mx-auto"
            >
              ← Back to Community Overview
            </button>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Community Forum</h1>
            <p className="text-xl text-gray-600">Share your biohacking journey and connect with fellow optimizers</p>
          </div>

          {/* New Post Button */}
          <div className="mb-8 flex justify-between items-center">
            <button
              onClick={() => setShowNewPostForm(!showNewPostForm)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2"
            >
              <span>📝</span>
              <span>Share Your Biohack</span>
            </button>
          </div>

          {/* New Post Form */}
          {showNewPostForm && (
            <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
              <form onSubmit={createPost} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    required
                    value={newPost.title}
                    onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                    placeholder="What's your biohacking discovery?"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newPost.category}
                    onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="general">General</option>
                    <option value="nutrition">Nutrition</option>
                    <option value="supplements">Supplements</option>
                    <option value="recovery">Recovery</option>
                    <option value="sleep">Sleep</option>
                    <option value="technology">Technology</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    required
                    rows={4}
                    value={newPost.content}
                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                    placeholder="Share your experience, results, and insights..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex space-x-4">
                  <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium">
                    Post
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowNewPostForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-2 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Posts */}
          <div className="space-y-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading posts...</p>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg">
                <div className="text-6xl mb-4">💬</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No posts yet</h3>
                <p className="text-gray-600 mb-6">Be the first to share your biohacking experience!</p>
                <button
                  onClick={() => setShowNewPostForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
                >
                  Create First Post
                </button>
              </div>
            ) : (
              posts.map((post) => (
                <div key={post.id} className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">{post.username.charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-gray-900">{post.username}</h3>
                          {(() => {
                            const levelInfo = getUserLevel(post.user_level || 'member');
                            return (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${levelInfo.color} flex items-center space-x-1`}>
                                <span>{levelInfo.icon}</span>
                                <span>{levelInfo.text}</span>
                              </span>
                            );
                          })()}
                        </div>
                        <p className="text-sm text-gray-500">{new Date(post.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      post.category === 'supplements' ? 'bg-purple-100 text-purple-700' :
                      post.category === 'recovery' ? 'bg-blue-100 text-blue-700' :
                      post.category === 'sleep' ? 'bg-indigo-100 text-indigo-700' :
                      post.category === 'nutrition' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                    </span>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-3">{post.title}</h2>
                  <div className="text-gray-700 mb-6 whitespace-pre-wrap">{post.content}</div>

                  {/* Reactions */}
                  <div className="flex items-center justify-between border-t pt-4">
                    <div className="flex items-center space-x-6">
                      <button
                        onClick={() => handleReaction(post.id, 'upvote')}
                        className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors"
                      >
                        <span className="text-lg">👍</span>
                        <span className="font-medium">{post.upvotes}</span>
                      </button>
                      <button
                        onClick={() => handleReaction(post.id, 'downvote')}
                        className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
                      >
                        <span className="text-lg">👎</span>
                        <span className="font-medium">{post.downvotes}</span>
                      </button>
                      <div className="text-gray-600">
                        <span className="text-lg">💬</span>
                        <span className="font-medium ml-2">{post.comments_count || 0}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      {Object.entries(post.reaction_counts || {}).map(([reaction, count]) => (
                        <button 
                          key={reaction}
                          onClick={() => handleReaction(post.id, reaction)}
                          className="flex items-center space-x-1 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                        >
                          <span>{
                            reaction === 'tried_this' ? '✅' :
                            reaction === 'helpful' ? '🔥' :
                            reaction === 'results' ? '📊' :
                            reaction === 'on_point' ? '🎯' : '👍'
                          }</span>
                          <span>{count}</span>
                          <span className="text-xs capitalize">{reaction.replace('_', ' ')}</span>
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          const postUrl = `${window.location.origin}/posts/${post.id}`;
                          if (navigator.share) {
                            navigator.share({
                              title: post.title,
                              text: `Check out this biohacking post: ${post.title}`,
                              url: postUrl,
                            });
                          } else {
                            navigator.clipboard.writeText(postUrl);
                            alert('Post link copied! Share it with fellow biohackers.');
                          }
                        }}
                        className="flex items-center space-x-1 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        <span>🔗</span>
                        <span className="text-xs">Share</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-14 bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Guidance, Community &amp; Learning
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Start a conversation with Raphael, our AI Wellness Coach, or take the 2-minute
              assessment. When you’re ready for more, we’ll personally match you with the
              right certified practitioner.
            </p>

            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
                <div className="text-gray-600">AI Coaching</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">1,000+</div>
                <div className="text-gray-600">Active Members</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/chat"
                className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors text-center"
              >
                Chat with Raphael
              </Link>
              <Link 
                to="/questionnaire"
                className="border-2 border-purple-600 text-purple-600 px-6 py-3 rounded-xl font-semibold hover:bg-purple-600 hover:text-white transition-all text-center"
              >
                Take the Free Assessment
              </Link>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Hackster AI Coach</h3>
              <p className="text-gray-600 mb-4">Get 24/7 personalized biohacking guidance with Raphael, our AI Wellness Coach, using the proprietary F.R.E.E.D.O.M. method.</p>
              <Link to="/chat" className="text-blue-600 font-semibold hover:text-blue-700">Try AI Coach →</Link>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Personalized Practitioner Matches</h3>
              <p className="text-gray-600 mb-4">Complete your assessment and we’ll match you with certified practitioners suited to your goals — no endless directory to sift through.</p>
              <Link to="/questionnaire" className="text-blue-600 font-semibold hover:text-blue-700">Get Matched →</Link>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 mb-3">The Hackster Library</h3>
              <p className="text-gray-600 mb-4">Learn the natural modalities behind lasting wellness — frequency healing, detox, sleep, mindfulness and more.</p>
              <Link to="/library" className="text-purple-600 font-semibold hover:text-purple-700">Explore the Library →</Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Community Landing Page Component (from LauraZook/Hackster repo)
const CommunityLanding = () => {
  const { isAuthenticated, user } = useAuth();
  const [showSignUp, setShowSignUp] = useState(false);

  if (showSignUp) {
    return <SignUpForm setShowSignUp={setShowSignUp} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <Navigation />

      {/* Dynamic Wellness Lifestyle Banner */}
      <div className="relative bg-gradient-to-r from-blue-800 to-purple-600 py-16 overflow-hidden">
        {/* Background Collage */}
        <div className="absolute inset-0 opacity-20">
          <div className="grid grid-cols-6 h-full">
            <div 
              className="bg-cover bg-center"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxoZWFsdGh5JTIwbGlmZXN0eWxlfGVufDB8fHx8MTc1NDE5MDY5N3ww&ixlib=rb-4.1.0&q=85')"
              }}
            ></div>
            <div 
              className="bg-cover bg-center"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1556911073-a517e752729c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxoZWFsdGh5JTIwbGlmZXN0eWxlfGVufDB8fHx8MTc1NDE5MDY5N3ww&ixlib=rb-4.1.0&q=85')"
              }}
            ></div>
            <div 
              className="bg-cover bg-center"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1607962837359-5e7e89f86776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxoZWFsdGh5JTIwbGlmZXN0eWxlfGVufDB8fHx8MTc1NDE5MDY5N3ww&ixlib=rb-4.1.0&q=85')"
              }}
            ></div>
            <div 
              className="bg-cover bg-center"
              style={{
                backgroundImage: "url('https://images.pexels.com/photos/1128678/pexels-photo-1128678.jpeg')"
              }}
            ></div>
            <div 
              className="bg-cover bg-center"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1542337010-818168f9bc1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHx3ZWxsbmVzcyUyMHBlb3BsZXxlbnwwfHx8fDE3NTQxOTA3MDh8MA&ixlib=rb-4.1.0&q=85')"
              }}
            ></div>
            <div 
              className="bg-cover bg-center"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1599948093964-321ae97fe7b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHx3ZWxsbmVzcyUyMHBlb3BsZXxlbnwwfHx8fDE3NTQxOTA3MDh8MA&ixlib=rb-4.1.0&q=85')"
              }}
            ></div>
          </div>
        </div>
        
        {/* Content Overlay */}
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Share Your Journey
          </h1>
          <p className="text-xl text-blue-100 mb-4 max-w-3xl mx-auto">
            Connect with fellow Hacksters! Share your experiences, discoveries, and insights.
          </p>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Join a supportive community of enthusiasts transforming their health daily.
          </p>
          
          {/* Activity Icons */}
          <div className="flex justify-center items-center space-x-8 mb-8 text-white">
            <div className="text-center">
              <div className="text-3xl mb-2">🥤</div>
              <p className="text-sm text-blue-100">Nutrition</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🏃‍♀️</div>
              <p className="text-sm text-blue-100">Exercise</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">🧘‍♀️</div>
              <p className="text-sm text-blue-100">Mindfulness</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">⌚</div>
              <p className="text-sm text-blue-100">Tracking</p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-2">😴</div>
              <p className="text-sm text-blue-100">Recovery</p>
            </div>
          </div>
          
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-6 max-w-md mx-auto">
            {isAuthenticated ? (
              <>
                <h3 className="text-xl font-semibold text-white mb-4">Welcome back, {user.username}!</h3>
                <p className="text-blue-100 mb-6 text-sm">
                  Ready to share your latest biohacking discoveries with the community?
                </p>
                <Link
                  to="/community-forum"
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors w-full block text-center"
                >
                  View Community Forum
                </Link>
              </>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-white mb-4">Ready to Connect?</h3>
                <p className="text-blue-100 mb-6 text-sm">
                  Please sign up to share your biohacking experiences and connect with the Hackster.ai community.
                </p>
                <Link
                  to="/login"
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors w-full block text-center"
                >
                  Join Community
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Features Preview */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <div>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-blue-600 text-2xl">💬</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Share Experiences</h3>
            <p className="text-gray-600 text-sm">Post your biohacking experiments, results, and discoveries with the community.</p>
          </div>
          <div>
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-purple-600 text-2xl">🤝</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Connect & Learn</h3>
            <p className="text-gray-600 text-sm">Engage with fellow Hacksters, comment on posts, and learn from each others' journey.</p>
          </div>
          <div>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-blue-600 text-2xl">📈</span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Track Progress</h3>
            <p className="text-gray-600 text-sm">Document your health journey and get feedback from experienced biohackers.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sign Up Form Component
const SignUpForm = ({ setShowSignUp }) => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Mock registration - replace with actual API call
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setShowSignUp(false);
      }, 2000);
    }, 1000);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <div className="text-green-600 text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Hackster!</h2>
          <p className="text-gray-600">Your account has been created successfully.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <Link to="/" className="flex items-center justify-center space-x-3 mb-6">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">H</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <span className="text-2xl font-bold text-gray-900">Hackster.ai</span>
                  <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-semibold">BETA</span>
                </div>
                <span className="text-xs text-gray-500 -mt-1">Your biohacking buddy</span>
              </div>
            </div>
          </Link>
          <h2 className="text-3xl font-extrabold text-gray-900">
            Join the Community
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Start your biohacking journey with fellow optimizers
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Username
              </label>
              <input
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Choose a username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Create a password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Join Community'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setShowSignUp(false)}
              className="text-blue-600 hover:text-blue-500 text-sm"
            >
              ← Back to Community
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// AI Coach Get Started Flow
const GetStartedFlow = () => {
  const [currentStep, setCurrentStep] = useState('welcome');
  const [userResponses, setUserResponses] = useState({
    hasBaselineTesting: null,
    healthGoals: [],
    currentSupplements: [],
    testingPreference: null,
    age: '',
    gender: '',
    activityLevel: ''
  });

  const handleResponse = (key, value) => {
    setUserResponses(prev => ({ ...prev, [key]: value }));
  };

  const nextStep = () => {
    switch(currentStep) {
      case 'welcome':
        setCurrentStep('baseline-check');
        break;
      case 'baseline-check':
        if (userResponses.hasBaselineTesting === false) {
          setCurrentStep('testing-options');
        } else {
          setCurrentStep('questionnaire');
        }
        break;
      case 'testing-options':
        if (userResponses.testingPreference === 'skip') {
          setCurrentStep('questionnaire');
        } else {
          setCurrentStep('testing-recommendations');
        }
        break;
      case 'testing-recommendations':
        setCurrentStep('questionnaire');
        break;
      case 'questionnaire':
        setCurrentStep('recommendations');
        break;
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-6 py-12">
        {currentStep === 'welcome' && (
          <div className="text-center">
            <div className="mb-8">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-3xl">🤖</span>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Welcome! I'm excited to be your biohacking buddy...</h1>
              <p className="text-xl text-gray-600 mb-8">
                Let's start with understanding where you are today and what goals we'll work on achieving.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">What I'll help you with:</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-3xl mb-3">🎯</div>
                  <h3 className="font-semibold mb-2">Establish a Vision</h3>
                  <p className="text-gray-600 text-sm">Identify the right health tests for benchmark data and then we'll set your specific goals</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-3">⚡</div>
                  <h3 className="font-semibold mb-2">Build Your Stack</h3>
                  <p className="text-gray-600 text-sm">Get personalized Hackster recommendations and build an epic stack that fits your lifestyle</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl mb-3">🚀</div>
                  <h3 className="font-semibold mb-2">Optimize Results</h3>
                  <p className="text-gray-600 text-sm">I'll be your biohacking buddy and connect you with expert live coaches when you're ready</p>
                </div>
              </div>
            </div>

            <button
              onClick={nextStep}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-colors"
            >
              Let's Start! →
            </button>
          </div>
        )}

        {currentStep === 'baseline-check' && (
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">First, let's talk about your health data</h2>
            <p className="text-lg text-gray-600 mb-8">
              Understanding your current health markers is crucial for effective biohacking. 
              Have you done any comprehensive health testing in the past 12 months?
            </p>

            <div className="space-y-4">
              <button
                onClick={() => {
                  handleResponse('hasBaselineTesting', true);
                  nextStep();
                }}
                className="w-full p-6 text-left bg-green-50 border-2 border-green-200 rounded-xl hover:bg-green-100 transition-colors"
              >
                <div className="text-xl font-semibold text-green-800 mb-2">✅ Yes, I have recent test results</div>
                <p className="text-green-700">I've done blood work, hormone panels, or other health testing recently</p>
              </button>

              <button
                onClick={() => {
                  handleResponse('hasBaselineTesting', false);
                  nextStep();
                }}
                className="w-full p-6 text-left bg-blue-50 border-2 border-blue-200 rounded-xl hover:bg-blue-100 transition-colors"
              >
                <div className="text-xl font-semibold text-blue-800 mb-2">📋 No, I need to get tested</div>
                <p className="text-blue-700">I haven't done comprehensive health testing recently</p>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'testing-options' && (
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Let's get your health baseline established</h2>
            <p className="text-lg text-gray-600 mb-8">
              Getting proper health testing is the foundation of effective biohacking. Would you like to see 
              our recommended testing options, or would you prefer to skip to building your supplement stack?
            </p>

            <div className="space-y-4">
              <button
                onClick={() => {
                  handleResponse('testingPreference', 'show-options');
                  nextStep();
                }}
                className="w-full p-6 text-left bg-emerald-50 border-2 border-emerald-200 rounded-xl hover:bg-emerald-100 transition-colors"
              >
                <div className="text-xl font-semibold text-emerald-800 mb-2">🎯 Show me testing recommendations</div>
                <p className="text-emerald-700">I want to see the best health testing options for my goals</p>
              </button>

              <button
                onClick={() => {
                  handleResponse('testingPreference', 'skip');
                  nextStep();
                }}
                className="w-full p-6 text-left bg-purple-50 border-2 border-purple-200 rounded-xl hover:bg-purple-100 transition-colors"
              >
                <div className="text-xl font-semibold text-purple-800 mb-2">⚡ Skip to supplement recommendations</div>
                <p className="text-purple-700">I'll handle testing later, show me supplements now</p>
              </button>
            </div>
          </div>
        )}

        {currentStep === 'testing-recommendations' && (
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Recommended Health Testing</h2>
            <p className="text-lg text-gray-600 mb-8">
              Based on your goals, here are our top recommendations for comprehensive health testing:
            </p>

            <div className="space-y-6 mb-8">
              <div className="border-2 border-blue-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Function Health - Complete Panel</h3>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">Most Popular</span>
                </div>
                <p className="text-gray-600 mb-4">110+ biomarkers including vitamins, minerals, hormones, and metabolic markers</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-blue-600">$499-699</span>
                  <a href="https://functionhealth.com" target="_blank" rel="noopener noreferrer" 
                     className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium">
                    Get Testing →
                  </a>
                </div>
              </div>

              <div className="border-2 border-purple-200 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Thorne - Personalized Testing</h3>
                <p className="text-gray-600 mb-4">Genetic testing + biomarker analysis for personalized supplement recommendations</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-purple-600">$149-299</span>
                  <a href="https://thorne.com" target="_blank" rel="noopener noreferrer" 
                     className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium">
                    Get Testing →
                  </a>
                </div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={nextStep}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-colors"
              >
                Continue to Questionnaire →
              </button>
            </div>
          </div>
        )}

        {currentStep === 'questionnaire' && (
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Smart Health Questionnaire</h2>
            <p className="text-lg text-gray-600 mb-8">
              Let's gather some information to create your personalized Hackster Stack:
            </p>

            <div className="space-y-6">
              <div>
                <label className="block text-lg font-semibold text-gray-700 mb-4">What are your primary health goals? (Select all that apply)</label>
                <div className="grid md:grid-cols-2 gap-3">
                  {['Increase Energy', 'Better Sleep', 'Improve Focus', 'Build Muscle', 'Lose Weight', 'Reduce Stress', 'Boost Immunity', 'Optimize Hormones'].map(goal => (
                    <button
                      key={goal}
                      onClick={() => {
                        const current = userResponses.healthGoals || [];
                        const updated = current.includes(goal) 
                          ? current.filter(g => g !== goal)
                          : [...current, goal];
                        handleResponse('healthGoals', updated);
                      }}
                      className={`p-3 text-left rounded-lg border-2 transition-colors ${
                        (userResponses.healthGoals || []).includes(goal)
                          ? 'bg-blue-100 border-blue-500 text-blue-800'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {goal}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-3">Age Range</label>
                  <select
                    value={userResponses.age}
                    onChange={(e) => handleResponse('age', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select age</option>
                    <option value="18-25">18-25</option>
                    <option value="26-35">26-35</option>
                    <option value="36-45">36-45</option>
                    <option value="46-55">46-55</option>
                    <option value="56-65">56-65</option>
                    <option value="65+">65+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-3">Gender</label>
                  <select
                    value={userResponses.gender}
                    onChange={(e) => handleResponse('gender', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-3">Activity Level</label>
                  <select
                    value={userResponses.activityLevel}
                    onChange={(e) => handleResponse('activityLevel', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select level</option>
                    <option value="sedentary">Sedentary</option>
                    <option value="light">Light Activity</option>
                    <option value="moderate">Moderate Activity</option>
                    <option value="active">Very Active</option>
                    <option value="athlete">Athlete</option>
                  </select>
                </div>
              </div>

              <div className="text-center pt-6">
                <button
                  onClick={nextStep}
                  disabled={!userResponses.healthGoals?.length || !userResponses.age || !userResponses.gender || !userResponses.activityLevel}
                  className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-colors"
                >
                  Get My Personalized Stack →
                </button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 'recommendations' && (
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Your Personalized Hackster Stack</h2>
            <p className="text-lg text-gray-600 mb-8">
              Based on your responses, here's your AI-powered supplement and biohacking recommendations:
            </p>

            <div className="space-y-6 mb-8">
              {/* Priority Supplements based on responses */}
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-xl font-bold text-blue-800 mb-3">🥇 Priority #1: Vitamin D3 + K2</h3>
                <p className="text-gray-600 mb-2">Essential for immune function, bone health, and mood regulation</p>
                <p className="text-sm text-blue-600 font-medium">Recommended: Thorne Vitamin D/K2 Liquid</p>
              </div>

              {userResponses.healthGoals?.includes('Increase Energy') && (
                <div className="border-l-4 border-green-500 pl-6">
                  <h3 className="text-xl font-bold text-green-800 mb-3">⚡ For Energy: Magnesium + B-Complex</h3>
                  <p className="text-gray-600 mb-2">Supports cellular energy production and reduces fatigue</p>
                  <p className="text-sm text-green-600 font-medium">Recommended: Thorne Magnesium Bisglycinate + Basic B Complex</p>
                </div>
              )}

              {userResponses.healthGoals?.includes('Better Sleep') && (
                <div className="border-l-4 border-purple-500 pl-6">
                  <h3 className="text-xl font-bold text-purple-800 mb-3">😴 For Sleep: Magnesium + L-Theanine</h3>
                  <p className="text-gray-600 mb-2">Promotes relaxation and improves sleep quality</p>
                  <p className="text-sm text-purple-600 font-medium">Recommended: Thorne Magnesium Bisglycinate + L-Theanine</p>
                </div>
              )}

              {userResponses.activityLevel === 'athlete' && (
                <div className="border-l-4 border-orange-500 pl-6">
                  <h3 className="text-xl font-bold text-orange-800 mb-3">🏃‍♂️ For Athletes: Essential Amino Acids</h3>
                  <p className="text-gray-600 mb-2">Supports muscle recovery and protein synthesis</p>
                  <p className="text-sm text-orange-600 font-medium">Recommended: Thorne Amino Complex</p>
                </div>
              )}

              <div className="border-l-4 border-gray-500 pl-6">
                <h3 className="text-xl font-bold text-gray-800 mb-3">🆓 Free Biohacks</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Morning sunlight exposure (10-30 minutes)</li>
                  <li>• Cold shower finish (30-90 seconds)</li>
                  <li>• Box breathing before bed (4-4-4-4 pattern)</li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/community" 
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-xl font-semibold text-center transition-colors"
              >
                Join Community to Share Results
              </Link>
              <Link 
                to="/questionnaire" 
                className="flex-1 border-2 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white px-6 py-4 rounded-xl font-semibold text-center transition-colors"
              >
                Get Matched with a Practitioner
              </Link>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

// Authentication Selection Page
const AuthSelectionPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <Navigation />
      
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Join Hackster.ai</h2>
          <p className="text-xl text-gray-600">Choose how you'd like to participate in our biohacking community</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Community Member Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 text-2xl">👥</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Join Community</h3>
              <p className="text-gray-600">Share your biohacking journey, connect with others, and learn from the community</p>
            </div>
            
            <div className="space-y-3 mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700">Share experiments and results</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700">Get personalized AI recommendations</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700">Access community forum</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-gray-700">Connect with fellow biohackers</span>
              </div>
            </div>

            <Link 
              to="/signup/member"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium text-center block transition-colors"
            >
              Join as a Member
            </Link>
          </div>

          {/* Coach Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow border-2 border-purple-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-purple-600 text-2xl">🎯</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Listed</h3>
              <p className="text-gray-600">Connect with Hackster.ai members to shape better, healthier futures.</p>
            </div>
            
            <div className="space-y-3 mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-gray-700">Create your professional profile</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-gray-700">Get client inquiries</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-gray-700">Free trial during beta</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-gray-700">$79/year after trial (no commissions!)</span>
              </div>
            </div>

            <Link 
              to="/signup/coach"
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium text-center block transition-colors"
            >
              Add Coach Profile
            </Link>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-600">Already have an account?</p>
          <Link to="/signin" className="text-blue-600 hover:text-blue-700 font-medium">
            Sign in here
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Sign In Page (for existing users)
const SignInPage = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        login(data.user, data.access_token);
        // Role-aware redirect — coaches go to their own dashboard
        if (data.user?.role === 'coach') {
          window.location.href = '/coach/dashboard';
        } else {
          window.location.href = '/dashboard';
        }
      } else {
        setError(data.detail || 'Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <Navigation />
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-gray-900">Welcome Back</h2>
          <p className="mt-2 text-sm text-gray-600">Sign in to your Hackster.ai account</p>
        </div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" className="text-blue-600 hover:text-blue-500">
              Need an account? Sign up
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Member Registration Page
const MemberSignUpPage = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, role: 'member' }),
      });

      const data = await response.json();

      if (response.ok) {
        login(data.user, data.access_token);
        window.location.href = '/dashboard'; // Redirect to dashboard
      } else {
        // Handle validation errors (422) and other errors
        if (Array.isArray(data.detail)) {
          setError(data.detail[0]?.msg || 'Registration failed');
        } else {
          setError(data.detail || 'Registration failed');
        }
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <Navigation />
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center mb-6">
            <span className="text-white text-2xl">👥</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Join the Community</h2>
          <p className="mt-2 text-lg text-gray-600">Start your biohacking journey with fellow optimizers</p>
        </div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Choose a username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Create a password"
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-blue-800 font-semibold mb-2">🎉 What you'll get:</h3>
              <ul className="text-blue-700 text-sm space-y-1">
                <li>• Access to the community forum</li>
                <li>• AI-powered biohacking recommendations</li>
                <li>• Connect with fellow biohackers</li>
                <li>• Share your experiments and results</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Join Community'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/signin" className="text-blue-600 hover:text-blue-500 text-sm">
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Coach Registration Page
const CoachSignUpPage = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, role: 'coach' }),
      });

      const data = await response.json();

      if (response.ok) {
        login(data.user, data.access_token);
        window.location.href = '/onboarding/coach'; // Redirect to coach onboarding
      } else {
        // Handle validation errors (422) and other errors
        if (Array.isArray(data.detail)) {
          setError(data.detail[0]?.msg || 'Registration failed');
        } else {
          setError(data.detail || 'Registration failed');
        }
      }
    } catch (err) {
      // Mock successful registration for demo
      login({ 
        username: formData.username, 
        email: formData.email, 
        role: 'coach' 
      }, 'mock-token');
      window.location.href = '/onboarding/coach';
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <Navigation />
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto h-12 w-12 bg-purple-600 rounded-lg flex items-center justify-center mb-6">
            <span className="text-white text-2xl">🎯</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Create Your Profile</h2>
          <p className="mt-2 text-lg text-gray-600">Join Hackster.ai's community and offer your services</p>
          <p className="mt-1 text-sm text-purple-600 font-medium">FREE Beta trial • All features included</p>
        </div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <div className="mb-6 p-4 bg-purple-50 rounded-lg">
            <h3 className="text-sm font-semibold text-purple-900 mb-2">Join our FREE BETA program:</h3>
            <ul className="text-sm text-purple-800 space-y-1">
              <li>• Complete professional profile</li>
              <li>• Direct client connections & inquiries</li>
              <li>• Full visibility in our coach directory</li>
              <li>• Help us build the best wellness platform</li>
              <li>• Early access to new features</li>
            </ul>
            <div className="mt-2 text-xs text-purple-600">
              <strong>Beta Promise:</strong> Your profile stays free during beta. We'll email you before any changes.
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Professional Email</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter your professional email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Choose your username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Create a secure password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Start Free Trial'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/signin" className="text-purple-600 hover:text-purple-500 text-sm">
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Coaches Page — fetches live coach directory from API
const CoachesPage = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/coaches`)
      .then(res => {
        setCoaches(Array.isArray(res.data) ? res.data : []);
        setError('');
      })
      .catch(err => {
        console.error('Failed to load coaches:', err);
        setError('Unable to load coach directory. Please try again later.');
      })
      .finally(() => setLoading(false));
  }, []);

  // Derive list of all specialties for filter dropdown
  const allSpecialties = Array.from(new Set(coaches.flatMap(c => c.specialties || []))).sort();

  const filteredCoaches = coaches.filter(c => {
    if (specialtyFilter && !(c.specialties || []).includes(specialtyFilter)) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${c.name || ''} ${c.bio || ''} ${(c.specialties || []).join(' ')} ${c.location || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Find a Wellness Coach</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Connect with certified health & wellness pros with biohacking expertise to accelerate your Hackster journey today!
          </p>
        </div>

        {/* Become a Coach Section */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-purple-600 rounded-lg p-8 mb-10 text-center text-white">
          <h2 className="text-xl font-semibold mb-3 leading-relaxed">
            Are you a wellness practitioner who loves transforming lives? Add your professional listing to the Hackster.ai community!
          </h2>
          <p className="mb-5">Share your expertise and help others. Join the Hackster.ai community for free.</p>
          <Link to="/signup/coach" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block">
            Join Now
          </Link>
        </div>

        {/* Search & Filter */}
        {!loading && coaches.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-col md:flex-row gap-3">
            <input
              type="text"
              placeholder="Search coaches by name, bio, location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={specialtyFilter}
              onChange={(e) => setSpecialtyFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">All specialties</option>
              {allSpecialties.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-sm text-gray-500 self-center">{filteredCoaches.length} coach{filteredCoaches.length === 1 ? '' : 'es'}</span>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading coaches…</p>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-center">{error}</div>
        )}

        {/* Coaches Grid */}
        {!loading && !error && filteredCoaches.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCoaches.map((coach) => {
              const email = coach.contact_info?.email;
              const phone = coach.contact_info?.phone;
              return (
                <div key={coach.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-xl transition-shadow flex flex-col">
                  <div className="text-center mb-3">
                    {coach.profile_image ? (
                      <img
                        src={coach.profile_image}
                        alt={`${coach.name} headshot`}
                        className="w-20 h-20 object-cover rounded-full mx-auto mb-3 border-4 border-blue-100"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full mx-auto mb-3 flex items-center justify-center">
                        <span className="text-white text-xl font-bold">
                          {(coach.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </span>
                      </div>
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 mb-0.5">{coach.name}</h3>
                    {coach.location && (
                      <p className="text-xs text-gray-500 mb-1">📍 {coach.location}{coach.years_experience ? ` · ${coach.years_experience} yrs exp` : ''}</p>
                    )}
                    {coach.rating > 0 && (
                      <div className="flex items-center justify-center gap-1 text-xs text-yellow-600 mb-2">
                        <span>★</span>
                        <span className="font-medium">{coach.rating?.toFixed?.(1) || coach.rating}</span>
                        <span className="text-gray-400">({coach.total_reviews || 0} reviews)</span>
                      </div>
                    )}
                    <div className="flex flex-wrap justify-center gap-1 mb-2">
                      {(coach.specialties || []).slice(0, 5).map((specialty) => (
                        <span key={specialty} className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full text-xs">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3 flex-1">
                    {coach.bio && (
                      <div>
                        <h4 className="font-semibold text-xs text-gray-500 uppercase mb-1">About</h4>
                        <p className="text-gray-600 text-sm leading-relaxed line-clamp-4">{coach.bio}</p>
                      </div>
                    )}

                    {coach.credentials && coach.credentials.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-xs text-gray-500 uppercase mb-1">Credentials</h4>
                        <div className="flex flex-wrap gap-1">
                          {coach.credentials.slice(0, 4).map(c => (
                            <span key={c} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {coach.hourly_rate && (
                      <div>
                        <h4 className="font-semibold text-xs text-gray-500 uppercase mb-0.5">Pricing</h4>
                        <p className="text-blue-600 font-semibold text-sm">{coach.hourly_rate}</p>
                      </div>
                    )}

                    {coach.availability && (
                      <div>
                        <h4 className="font-semibold text-xs text-gray-500 uppercase mb-0.5">Availability</h4>
                        <p className="text-gray-700 text-sm">{coach.availability}</p>
                      </div>
                    )}

                    {coach.website && (
                      <div>
                        <a
                          href={coach.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                        >
                          🌐 Visit website
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                    {email && (
                      <a
                        href={`mailto:${email}`}
                        className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg font-medium transition-colors text-sm"
                      >
                        📧 Contact Coach
                      </a>
                    )}
                    {phone && (
                      <a
                        href={`tel:${phone}`}
                        className="block w-full text-center border border-blue-200 text-blue-700 hover:bg-blue-50 py-2 px-4 rounded-lg font-medium transition-colors text-sm"
                      >
                        📞 {phone}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filteredCoaches.length === 0 && (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {coaches.length === 0 ? 'No coaches yet' : 'No coaches match your filters'}
            </h3>
            <p className="text-gray-600 mb-6">
              {coaches.length === 0
                ? 'Be among the first wellness coaches to join our community!'
                : 'Try clearing your search or selecting a different specialty.'}
            </p>
            {coaches.length === 0 ? (
              <Link to="/signup/coach" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium">
                Become the First Coach
              </Link>
            ) : (
              <button onClick={() => { setSearch(''); setSpecialtyFilter(''); }} className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-medium">
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Alias for backward compatibility
const CommunityCoaching = CommunityPlatform;

// Main Home Component
const Home = () => {
  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log("Backend connected:", response.data.message);
    } catch (e) {
      console.error("Backend connection error:", e);
    }
  };

  useEffect(() => {
    helloWorldApi();
  }, []);

  return (
    <div className="min-h-screen">
      <HeroSection />
      <CoreFeatures />
      <PrioritySupplements />
      <CommunityCoaching />
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            {/* Logo and Description */}
            <div className="md:col-span-1">
              <Link to="/" className="flex items-center space-x-3 mb-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">H</span>
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center space-x-2">
                      <span className="text-xl font-bold">Hackster.ai</span>
                      <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-semibold">BETA</span>
                    </div>
                    <span className="text-xs text-gray-400 -mt-1">Your biohacking buddy</span>
                  </div>
                </div>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed">
                Empowering your biohacking journey with AI-powered recommendations, expert guidance, and a supportive community.
              </p>
            </div>

            {/* Platform Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Platform</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/get-started" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
                    Get Started
                  </Link>
                </li>
                <li>
                  <Link to="/community" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
                    Community
                  </Link>
                </li>
                <li>
                  <Link to="/coaches" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
                    Find a Coach
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
                    Join Now
                  </Link>
                </li>
              </ul>
            </div>

            {/* For Professionals */}
            <div>
              <h3 className="text-white font-semibold mb-4">For Professionals</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/signup/coach" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">
                    Become a Coach
                  </Link>
                </li>
                <li>
                  <Link to="/coaches" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">
                    Coach Directory
                  </Link>
                </li>
                <li>
                  <Link to="/signin" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">
                    Coach Login
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources & Legal */}
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/chat" className="text-gray-400 hover:text-green-400 transition-colors text-sm">
                    Chat with Raphael AI
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-gray-400 hover:text-blue-400 transition-colors text-sm">
                    Experiments Blog
                    <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">Phase 2</span>
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <a href="mailto:support@hackster.ai" className="text-gray-400 hover:text-white transition-colors text-sm">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Disclaimer Section */}
          <div className="border-t border-gray-800 pt-8 mb-8">
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-white font-semibold mb-4">
                Medical Disclaimer
              </h3>
              <div className="text-gray-300 text-sm leading-relaxed space-y-3">
                <p>
                  The information provided by Hackster.ai, including AI recommendations, community discussions, and coach guidance, is for educational, research and informational purposes only. Always consult with qualified healthcare providers before making any changes to your health regimen, starting new supplements, or implementing biohacking protocols. Individual results may vary.
                </p>
                <p>
                  Any recommendations on Hackster.ai are not intended to diagnose, treat, cure, or prevent any diseases.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2026 Hackster.ai is a PLZ Company brand. All rights reserved. Beta Version.
              </div>
              <div className="flex items-center space-x-6">
                <span className="text-gray-400 text-sm">
                  Made with ❤️ for the biohacking community
                </span>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 text-sm">Beta Live</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ============================================================================
// COACH ONBOARDING — Multi-step Wizard
// ============================================================================
const COACH_SPECIALTIES = [
  // Aligned with our questionnaire / coach-matching keywords
  'Energy', 'Vitality', 'Longevity', 'Immune Support',
  'Weight Loss', 'Weight Management', 'Metabolic Health',
  'Bioenergy', 'Frequency Therapy', 'Stem Cell Health',
  'Hormone Optimization', 'Gut Health', 'Stress Management',
  'Sleep Optimization', 'Cold Therapy', 'Breathwork',
  'Athletic Performance', 'Functional Medicine', 'Biohacking',
  'Nutrition', 'Supplements', 'Recovery'
];

const COACH_DRAFT_KEY = 'hackster_coach_draft';

const CoachOnboardingPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load draft from localStorage (if any)
  const loadDraft = () => {
    try {
      const raw = localStorage.getItem(COACH_DRAFT_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) { /* ignore */ }
    return null;
  };

  const [profile, setProfile] = useState(() => loadDraft() || {
    full_name: '',
    profile_image: '',
    location: '',
    years_experience: '',
    specialties: [],
    credentials: [],
    bio: '',
    hourly_rate: '',
    availability: 'Mon-Fri 9AM-6PM (Virtual)',
    phone: '',
    website: ''
  });
  const [credentialInput, setCredentialInput] = useState('');

  // Persist draft on every change
  useEffect(() => {
    localStorage.setItem(COACH_DRAFT_KEY, JSON.stringify(profile));
  }, [profile]);

  // If they already have a published profile, send them to the dashboard
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/coaches/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (res.data && res.data.id) {
          // Already published — redirect to dashboard
          navigate('/coach/dashboard');
        }
      })
      .catch(() => { /* not a coach yet, fine */ });
  }, [token, navigate]);

  const update = (patch) => setProfile(prev => ({ ...prev, ...patch }));

  const toggleSpecialty = (s) => {
    update({
      specialties: profile.specialties.includes(s)
        ? profile.specialties.filter(x => x !== s)
        : [...profile.specialties, s]
    });
  };

  const addCredential = () => {
    const v = credentialInput.trim();
    if (!v) return;
    if (profile.credentials.includes(v)) {
      setCredentialInput('');
      return;
    }
    update({ credentials: [...profile.credentials, v] });
    setCredentialInput('');
  };

  const removeCredential = (c) => {
    update({ credentials: profile.credentials.filter(x => x !== c) });
  };

  const stepValid = () => {
    if (step === 1) return profile.full_name.trim() && profile.location.trim();
    if (step === 2) return profile.specialties.length >= 1;
    if (step === 3) return profile.bio.trim().length >= 30;
    if (step === 4) return profile.hourly_rate.trim();
    return true;
  };

  const saveDraftAndExit = () => {
    localStorage.setItem(COACH_DRAFT_KEY, JSON.stringify(profile));
    navigate('/coach/dashboard');
  };

  const submitProfile = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/coaches`, {
        name: profile.full_name,
        bio: profile.bio,
        specialties: profile.specialties,
        location: profile.location,
        hourly_rate: profile.hourly_rate,
        availability: profile.availability,
        credentials: profile.credentials,
        contact_info: {
          email: user?.email || '',
          phone: profile.phone || '',
          website: profile.website || ''
        },
        profile_image: profile.profile_image || null,
        website: profile.website || null,
        years_experience: profile.years_experience ? parseInt(profile.years_experience) : null
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.status === 200) {
        localStorage.removeItem(COACH_DRAFT_KEY);
        navigate('/coach/dashboard?welcome=1');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not publish your profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = Math.round((step / totalSteps) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pb-16">
      <Navigation />

      <div className="max-w-3xl mx-auto px-4 pt-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto h-14 w-14 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center mb-4">
            <span className="text-white text-2xl">🎯</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Build Your Coach Profile</h1>
          <p className="text-gray-600 mt-2">Takes about 3 minutes. Your draft auto-saves as you go.</p>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs font-medium text-gray-600 mb-2">
            <span>Step {step} of {totalSteps}</span>
            <span>{progressPct}% complete</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-300" style={{ width: `${progressPct}%` }}></div>
          </div>
          <div className="flex justify-between mt-3 text-xs text-gray-500">
            <span className={step >= 1 ? 'text-purple-600 font-semibold' : ''}>1. Basics</span>
            <span className={step >= 2 ? 'text-purple-600 font-semibold' : ''}>2. Expertise</span>
            <span className={step >= 3 ? 'text-purple-600 font-semibold' : ''}>3. Your Story</span>
            <span className={step >= 4 ? 'text-purple-600 font-semibold' : ''}>4. Pricing</span>
          </div>
        </div>

        {/* Step Card */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          {/* STEP 1: Basics */}
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900">Tell us the basics</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={profile.full_name}
                  onChange={(e) => update({ full_name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Dr. Jane Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo URL <span className="text-gray-400 font-normal">(optional)</span></label>
                <div className="flex items-center gap-3">
                  {profile.profile_image ? (
                    <img src={profile.profile_image} alt="profile" className="w-14 h-14 rounded-full object-cover border" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-200 to-blue-200 flex items-center justify-center text-purple-600 font-bold text-lg">
                      {profile.full_name ? profile.full_name.split(' ').map(n => n[0]).join('').slice(0, 2) : '👤'}
                    </div>
                  )}
                  <input
                    type="url"
                    value={profile.profile_image}
                    onChange={(e) => update({ profile_image: e.target.value })}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="https://yoursite.com/photo.jpg"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Paste a public URL to your headshot. We'll add direct uploads soon.</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                  <input
                    type="text"
                    value={profile.location}
                    onChange={(e) => update({ location: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., Austin, TX (Virtual)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Years of Experience</label>
                  <input
                    type="number"
                    value={profile.years_experience}
                    onChange={(e) => update({ years_experience: e.target.value })}
                    min="0" max="60"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="5"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Expertise */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900">Your expertise</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specialties * <span className="text-gray-400 font-normal">(pick all that apply — we use these to match you with clients)</span></label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {COACH_SPECIALTIES.map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleSpecialty(s)}
                      className={`px-3 py-2 rounded-lg text-sm text-left border transition-colors ${
                        profile.specialties.includes(s)
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-purple-400'
                      }`}
                    >
                      {profile.specialties.includes(s) ? '✓ ' : ''}{s}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">{profile.specialties.length} selected</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Credentials & Certifications <span className="text-gray-400 font-normal">(optional)</span></label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={credentialInput}
                    onChange={(e) => setCredentialInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCredential(); } }}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., NASM-CPT, RD, FMCA, Bio-Well Certified"
                  />
                  <button type="button" onClick={addCredential} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Add</button>
                </div>
                {profile.credentials.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {profile.credentials.map(c => (
                      <span key={c} className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full">
                        {c}
                        <button onClick={() => removeCredential(c)} className="ml-1 text-purple-500 hover:text-purple-700" aria-label={`Remove ${c}`}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 3: Bio / Story */}
          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900">Your story</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Professional Bio * <span className="text-gray-400 font-normal">(at least 30 characters)</span></label>
                <textarea
                  rows={7}
                  value={profile.bio}
                  onChange={(e) => update({ bio: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="What's your background, who do you love working with, and what's your approach to coaching? Be specific — this is what attracts the right clients."
                />
                <p className={`text-xs mt-1 ${profile.bio.length >= 30 ? 'text-green-600' : 'text-gray-500'}`}>
                  {profile.bio.length} / 30 characters minimum
                </p>
              </div>
            </div>
          )}

          {/* STEP 4: Pricing & Contact */}
          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-xl font-bold text-gray-900">Pricing & availability</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pricing *</label>
                  <input
                    type="text"
                    value={profile.hourly_rate}
                    onChange={(e) => update({ hourly_rate: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., $150/session or $500/month"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Availability</label>
                  <input
                    type="text"
                    value={profile.availability}
                    onChange={(e) => update({ availability: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Mon-Fri 9AM-5PM CT (Virtual)"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => update({ phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Website <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="url"
                    value={profile.website}
                    onChange={(e) => update({ website: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="https://yoursite.com"
                  />
                </div>
              </div>

              {/* Live preview */}
              <div className="mt-6 p-5 border-2 border-dashed border-purple-200 rounded-xl bg-gradient-to-b from-white to-purple-50/40">
                <p className="text-xs font-semibold text-purple-700 uppercase mb-3">📋 Preview — how members will see you</p>
                <div className="flex items-start gap-3">
                  {profile.profile_image ? (
                    <img src={profile.profile_image} alt="profile preview" className="w-16 h-16 rounded-full object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-xl">
                      {(profile.full_name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900">{profile.full_name || 'Your Name'}</h3>
                    <p className="text-xs text-gray-500">{profile.location || 'Location'} {profile.years_experience ? `· ${profile.years_experience} yrs exp` : ''}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {profile.specialties.slice(0, 4).map(s => (
                        <span key={s} className="text-[11px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{s}</span>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{profile.bio || 'Your bio will appear here…'}</p>
                    <div className="text-sm font-medium text-gray-800 mt-2">{profile.hourly_rate || 'Pricing'}</div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm text-blue-900">
                <strong>🚀 You go live instantly during beta.</strong> No approval queue — your profile publishes the moment you click "Publish".
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm">{error}</div>
          )}

          {/* Wizard footer */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            <div className="flex gap-2">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  ← Back
                </button>
              )}
              <button
                type="button"
                onClick={saveDraftAndExit}
                className="px-5 py-2.5 text-gray-500 hover:text-gray-700 text-sm"
              >
                Save & Exit
              </button>
            </div>
            {step < totalSteps ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                disabled={!stepValid()}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            ) : (
              <button
                type="button"
                onClick={submitProfile}
                disabled={!stepValid() || submitting}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? 'Publishing…' : '🚀 Publish My Profile'}
              </button>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// ============================================================================
// COACH DASHBOARD — landing page for authenticated coaches
// ============================================================================
const CoachDashboardPage = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [coachProfile, setCoachProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draftExists, setDraftExists] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    if (window.location.search.includes('welcome=1')) {
      setShowWelcome(true);
      setTimeout(() => setShowWelcome(false), 6000);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    setDraftExists(!!localStorage.getItem(COACH_DRAFT_KEY));
    axios.get(`${API}/coaches/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setCoachProfile(res.data || null))
      .catch(() => setCoachProfile(null))
      .finally(() => setLoading(false));
  }, [isAuthenticated, token]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="text-6xl mb-6">🔐</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Sign in to your coach dashboard</h1>
          <Link to="/signin" className="bg-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-purple-700">Sign In</Link>
        </div>
      </div>
    );
  }

  if (user?.role !== 'coach') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">This area is for coaches</h1>
          <p className="text-gray-600 mb-6">You're signed in as a member.</p>
          <Link to="/dashboard" className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold">Go to Member Dashboard</Link>
        </div>
      </div>
    );
  }

  // Profile completeness scoring
  const fields = coachProfile ? [
    !!coachProfile.name,
    !!coachProfile.bio && coachProfile.bio.length >= 30,
    coachProfile.specialties?.length > 0,
    !!coachProfile.location,
    !!coachProfile.hourly_rate,
    !!coachProfile.availability,
    !!coachProfile.profile_image,
    coachProfile.credentials?.length > 0,
    !!coachProfile.website,
    !!coachProfile.years_experience
  ] : [];
  const completeness = fields.length ? Math.round((fields.filter(Boolean).length / fields.length) * 100) : 0;

  const publicShareUrl = coachProfile ? `${window.location.origin}/coaches` : '';
  const isLive = coachProfile && coachProfile.is_approved && coachProfile.is_active;

  const handleShare = () => {
    if (!publicShareUrl) return;
    navigator.clipboard?.writeText(publicShareUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2500);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      {/* Welcome toast */}
      {showWelcome && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-xl shadow-xl animate-pulse">
          🎉 You're live! Your coach profile is published.
        </div>
      )}

      {/* Banner */}
      <div className={`${isLive ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-purple-600 to-blue-600'} text-white py-10`}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-1">Hi, {coachProfile?.name?.split(' ')[0] || user?.username} 👋</h1>
            <p className="text-white/90">
              {!coachProfile && 'Welcome to Hackster.ai — let\'s get your coach profile live.'}
              {coachProfile && isLive && '✅ Your profile is LIVE in the Hackster coach directory.'}
              {coachProfile && !isLive && '⏸ Your profile is currently deactivated. Contact support to reactivate.'}
            </p>
          </div>
          {isLive && (
            <div className="flex gap-2">
              <Link to="/coaches" className="bg-white text-emerald-700 font-semibold px-4 py-2 rounded-lg hover:bg-gray-50 text-sm">View in Directory</Link>
              <button onClick={handleShare} className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-4 py-2 rounded-lg text-sm">
                {shareCopied ? '✓ Copied!' : '🔗 Share'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your dashboard…</p>
          </div>
        ) : !coachProfile ? (
          // === NO PROFILE YET ===
          <div className="bg-white rounded-xl shadow-md p-8 text-center max-w-2xl mx-auto">
            <div className="text-5xl mb-4">📝</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              {draftExists ? 'Continue your profile' : 'Create your coach profile'}
            </h2>
            <p className="text-gray-600 mb-6">
              {draftExists
                ? 'You have a saved draft. Pick up where you left off — your profile auto-publishes when you finish.'
                : 'Set up your professional profile in about 3 minutes. You\'ll go live in the directory instantly.'}
            </p>
            <Link to="/onboarding/coach" className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:opacity-90">
              {draftExists ? 'Continue draft →' : 'Start onboarding →'}
            </Link>
          </div>
        ) : (
          // === HAS PROFILE ===
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left: profile card preview */}
            <div className="lg:col-span-2 space-y-6">
              {/* Status + completeness */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900">Profile Status</h3>
                  {isLive ? (
                    <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">● LIVE</span>
                  ) : (
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-3 py-1 rounded-full">⏸ Inactive</span>
                  )}
                </div>
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-sm text-gray-600">Profile completeness</span>
                  <span className="text-sm font-semibold text-gray-900">{completeness}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`h-full transition-all ${completeness === 100 ? 'bg-green-500' : completeness >= 70 ? 'bg-blue-500' : 'bg-yellow-500'}`} style={{ width: `${completeness}%` }}></div>
                </div>
                {completeness < 100 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Add the missing fields (credentials, profile photo, website, years of experience) to get more visibility in matches.
                  </p>
                )}
              </div>

              {/* Public profile preview */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">Public Profile Preview</h3>
                  <Link to="/coach/profile/edit" className="text-sm text-purple-600 hover:text-purple-700 font-medium">Edit profile →</Link>
                </div>
                <div className="border border-gray-200 rounded-xl p-5 bg-gradient-to-b from-white to-blue-50/30">
                  <div className="flex items-start gap-4">
                    {coachProfile.profile_image ? (
                      <img src={coachProfile.profile_image} alt={coachProfile.name} className="w-20 h-20 rounded-full object-cover border-2 border-purple-100" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-2xl">
                        {coachProfile.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-900">{coachProfile.name}</h4>
                      <p className="text-sm text-gray-500">{coachProfile.location}{coachProfile.years_experience ? ` · ${coachProfile.years_experience} yrs experience` : ''}</p>
                      <div className="flex items-center gap-1 text-sm text-yellow-600 mt-1">
                        <span>★</span><span className="font-medium">{coachProfile.rating?.toFixed?.(1) || '—'}</span>
                        <span className="text-gray-400">({coachProfile.total_reviews || 0} reviews)</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(coachProfile.specialties || []).slice(0, 6).map(s => (
                          <span key={s} className="text-[11px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{s}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mt-4 leading-relaxed">{coachProfile.bio}</p>
                  {coachProfile.credentials?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Credentials</p>
                      <div className="flex flex-wrap gap-1">
                        {coachProfile.credentials.map(c => (
                          <span key={c} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-100 text-sm">
                    <div><span className="text-gray-500">Pricing:</span> <span className="text-gray-900 font-medium">{coachProfile.hourly_rate}</span></div>
                    <div><span className="text-gray-500">Availability:</span> <span className="text-gray-900">{coachProfile.availability}</span></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: stats + quick actions */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-bold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  <Link to="/coach/profile/edit" className="flex items-center gap-3 p-3 rounded-lg hover:bg-purple-50 text-gray-700 hover:text-purple-700">
                    <span>✏️</span><span className="font-medium">Edit my profile</span>
                  </Link>
                  <Link to="/coaches" className="flex items-center gap-3 p-3 rounded-lg hover:bg-purple-50 text-gray-700 hover:text-purple-700">
                    <span>👀</span><span className="font-medium">View public directory</span>
                  </Link>
                  <button onClick={handleShare} className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-purple-50 text-gray-700 hover:text-purple-700 text-left">
                    <span>🔗</span><span className="font-medium">{shareCopied ? '✓ Link copied!' : 'Copy share link'}</span>
                  </button>
                  <Link to="/community-forum" className="flex items-center gap-3 p-3 rounded-lg hover:bg-purple-50 text-gray-700 hover:text-purple-700">
                    <span>💬</span><span className="font-medium">Engage in Community</span>
                  </Link>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-bold text-gray-900 mb-4">Performance</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Profile views</span>
                    <span className="text-lg font-bold text-gray-900">—</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Inquiries</span>
                    <span className="text-lg font-bold text-gray-900">—</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Average rating</span>
                    <span className="text-lg font-bold text-yellow-600">{coachProfile.rating?.toFixed?.(1) || '—'}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-3">Analytics coming soon.</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-100 rounded-xl p-5">
                <h4 className="font-bold text-gray-900 mb-2">💡 Pro tip</h4>
                <p className="text-sm text-gray-700">Add at least one credential and a profile photo — coaches with complete profiles get matched 3× more often by our AI questionnaire.</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

// ============================================================================
// COACH PROFILE EDIT — full editor for existing coach
// ============================================================================
const CoachProfileEditPage = () => {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [credentialInput, setCredentialInput] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    axios.get(`${API}/coaches/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        if (!res.data) {
          navigate('/onboarding/coach');
          return;
        }
        setProfile({
          ...res.data,
          phone: res.data.contact_info?.phone || '',
        });
      })
      .catch(() => navigate('/onboarding/coach'))
      .finally(() => setLoading(false));
  }, [isAuthenticated, token, navigate]);

  if (!isAuthenticated || user?.role !== 'coach') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Coach access only.</h1>
          <Link to="/signin" className="text-blue-600">Sign in</Link>
        </div>
      </div>
    );
  }

  const update = (patch) => setProfile(prev => ({ ...prev, ...patch }));

  const toggleSpecialty = (s) => {
    update({
      specialties: profile.specialties?.includes(s)
        ? profile.specialties.filter(x => x !== s)
        : [...(profile.specialties || []), s]
    });
  };

  const addCredential = () => {
    const v = credentialInput.trim();
    if (!v) return;
    if (!(profile.credentials || []).includes(v)) {
      update({ credentials: [...(profile.credentials || []), v] });
    }
    setCredentialInput('');
  };

  const removeCredential = (c) => {
    update({ credentials: (profile.credentials || []).filter(x => x !== c) });
  };

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const payload = {
        name: profile.name,
        bio: profile.bio,
        specialties: profile.specialties,
        location: profile.location,
        hourly_rate: profile.hourly_rate,
        availability: profile.availability,
        credentials: profile.credentials || [],
        contact_info: {
          email: user?.email || profile.contact_info?.email || '',
          phone: profile.phone || '',
          website: profile.website || ''
        },
        profile_image: profile.profile_image || null,
        website: profile.website || null,
        years_experience: profile.years_experience ? parseInt(profile.years_experience) : null
      };
      const res = await axios.patch(`${API}/coaches/me`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile({ ...res.data, phone: res.data.contact_info?.phone || '' });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/coach/dashboard" className="text-sm text-gray-500 hover:text-gray-700">← Back to dashboard</Link>
            <h1 className="text-3xl font-bold text-gray-900 mt-1">Edit Profile</h1>
          </div>
          {saved && <span className="text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full text-sm">✓ Saved</span>}
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 md:p-8 space-y-6">
          {/* Basics */}
          <section>
            <h2 className="font-bold text-gray-900 mb-3">Basics</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input type="text" value={profile.name || ''} onChange={(e) => update({ name: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profile Photo URL</label>
                <input type="url" value={profile.profile_image || ''} onChange={(e) => update({ profile_image: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="https://…" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input type="text" value={profile.location || ''} onChange={(e) => update({ location: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                  <input type="number" min="0" max="60" value={profile.years_experience ?? ''} onChange={(e) => update({ years_experience: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>
            </div>
          </section>

          {/* Specialties */}
          <section>
            <h2 className="font-bold text-gray-900 mb-3">Specialties <span className="text-sm text-gray-500 font-normal">(used to match you with clients)</span></h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {COACH_SPECIALTIES.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSpecialty(s)}
                  className={`px-3 py-2 rounded-lg text-sm text-left border transition-colors ${profile.specialties?.includes(s) ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-200 hover:border-purple-400'}`}
                >
                  {profile.specialties?.includes(s) ? '✓ ' : ''}{s}
                </button>
              ))}
            </div>
          </section>

          {/* Credentials */}
          <section>
            <h2 className="font-bold text-gray-900 mb-3">Credentials & Certifications</h2>
            <div className="flex gap-2">
              <input type="text" value={credentialInput} onChange={(e) => setCredentialInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCredential(); } }} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="e.g., RD, NASM-CPT, Bio-Well Certified" />
              <button type="button" onClick={addCredential} className="px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Add</button>
            </div>
            {(profile.credentials || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.credentials.map(c => (
                  <span key={c} className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full">
                    {c}<button onClick={() => removeCredential(c)} className="ml-1 text-purple-500 hover:text-purple-700">×</button>
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Bio */}
          <section>
            <h2 className="font-bold text-gray-900 mb-3">Bio</h2>
            <textarea rows={6} value={profile.bio || ''} onChange={(e) => update({ bio: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
          </section>

          {/* Pricing + Availability */}
          <section className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pricing</label>
              <input type="text" value={profile.hourly_rate || ''} onChange={(e) => update({ hourly_rate: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Availability</label>
              <input type="text" value={profile.availability || ''} onChange={(e) => update({ availability: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
            </div>
          </section>

          {/* Contact */}
          <section className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="tel" value={profile.phone || ''} onChange={(e) => update({ phone: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input type="url" value={profile.website || ''} onChange={(e) => update({ website: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500" />
            </div>
          </section>

          {error && <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm">{error}</div>}

          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <Link to="/coach/dashboard" className="text-gray-600 hover:text-gray-800">Cancel</Link>
            <button onClick={save} disabled={saving} className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Blog System (Phase 2 - Prepared for future)
const BlogPage = () => {
  const [posts, setPosts] = useState([]);

  // Sample blog posts for Phase 2
  const samplePosts = [
    {
      id: 1,
      title: "The Science Behind Cold Exposure: A 30-Day Experiment",
      excerpt: "I spent 30 days incorporating cold showers and ice baths into my routine. Here's what the data showed...",
      author: "Dr. Sarah Martinez",
      date: "2024-12-15",
      category: "Experiments",
      readTime: "8 min read",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800",
      content: "Detailed experiment results and methodology..."
    },
    {
      id: 2,
      title: "Optimizing Sleep with Red Light Therapy: My 60-Day Journey",
      excerpt: "Testing red light therapy's impact on sleep quality using HRV and sleep tracking data...",
      author: "Mike Chen",
      date: "2024-12-10",
      category: "Sleep Optimization",
      readTime: "12 min read",
      image: "https://images.unsplash.com/photo-1520637836862-4d197d17c8a4?w=800",
      content: "Comprehensive sleep data analysis and protocol..."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Hackster Experiments</h1>
          <p className="text-xl text-gray-600">Real biohacking experiments with data-driven results</p>
        </div>

        <div className="space-y-8">
          {samplePosts.map(post => (
            <article key={post.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <img src={post.image} alt={post.title} className="w-full h-48 object-cover" />
              <div className="p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    {post.category}
                  </span>
                  <span className="text-gray-500 text-sm">{post.readTime}</span>
                  <span className="text-gray-500 text-sm">{post.date}</span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{post.title}</h2>
                <p className="text-gray-600 mb-4">{post.excerpt}</p>
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 text-sm">By {post.author}</span>
                  <button className="text-blue-600 hover:text-blue-700 font-medium">
                    Read Experiment →
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-12 p-8 bg-blue-50 rounded-xl">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Coming in Phase 2</h3>
          <p className="text-gray-600">Full blog system with community experiments, detailed protocols, and data analysis</p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Coaching Landing Page - Choose AI Coach or Human Coaches
const CoachingLandingPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Your Hackster Support Team</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Whether you need instant guidance or personalized 1:1 coaching, we have the right support for your journey.
          </p>
        </div>
      </div>

      {/* Two Options */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* AI Coach - Raphael */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-6 text-white">
              <div className="text-5xl mb-4">🌟</div>
              <h2 className="text-2xl font-bold">Meet Raphael</h2>
              <p className="text-purple-100">Your 24/7 AI Wellness Coach</p>
            </div>
            <div className="p-6">
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-purple-500 mt-1">✓</span>
                  <span className="text-gray-700">Available 24/7, instant responses anytime</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-500 mt-1">✓</span>
                  <span className="text-gray-700">Trained in the F.R.E.E.D.O.M. Health & Wellness Method</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-500 mt-1">✓</span>
                  <span className="text-gray-700">Personalized supplement & product recommendations</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-500 mt-1">✓</span>
                  <span className="text-gray-700">Educational guidance on how your body works</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-purple-500 mt-1">✓</span>
                  <span className="text-gray-700">Free to start - 7 messages before sign-up</span>
                </li>
              </ul>
              <p className="text-gray-600 text-sm mb-6">
                Raphael is your always-available wellness guide, ready to answer questions, provide recommendations, 
                and teach you about holistic health using the F.R.E.E.D.O.M. method.
              </p>
              <button
                onClick={() => navigate('/chat')}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-purple-600 hover:to-indigo-600 transition-all"
              >
                Chat with Raphael →
              </button>
            </div>
          </div>

          {/* Human Coaches */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-6 text-white">
              <div className="text-5xl mb-4">🙋‍♀️</div>
              <h2 className="text-2xl font-bold">Find a Health Coach</h2>
              <p className="text-blue-100">Certified Wellness Experts</p>
            </div>
            <div className="p-6">
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <span className="text-blue-500 mt-1">✓</span>
                  <span className="text-gray-700">Personalized 1:1 coaching sessions</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-500 mt-1">✓</span>
                  <span className="text-gray-700">Certified professionals in nutrition, fitness & wellness</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-500 mt-1">✓</span>
                  <span className="text-gray-700">Accountability and ongoing support</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-500 mt-1">✓</span>
                  <span className="text-gray-700">Customized health plans tailored to your goals</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-500 mt-1">✓</span>
                  <span className="text-gray-700">Human connection and empathy</span>
                </li>
              </ul>
              <p className="text-gray-600 text-sm mb-6">
                Connect with certified wellness coaches who can provide personalized guidance, create custom plans, 
                and hold you accountable on your health transformation journey.
              </p>
              <button
                onClick={() => navigate('/coaches')}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all"
              >
                Browse Coaches →
              </button>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-white rounded-2xl p-8 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Not sure where to start?</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Try chatting with Raphael first! Our AI coach can help you understand your needs, 
            answer initial questions, and even recommend the right human coach for your specific goals.
          </p>
          <button
            onClick={() => navigate('/chat')}
            className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-3 px-8 rounded-xl font-semibold hover:from-purple-600 hover:to-indigo-600 transition-all"
          >
            Start with Raphael →
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// AI Coach Chat - Raphael (The Healer)
const AICoachChat = () => {
  const { isAuthenticated, user, token } = useAuth();
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'raphael',
      text: "✨ Welcome! I'm Raphael, your Hackster AI Wellness Coach. I'm here to support you on your journey to better health and vitality.\n\nI use the F.R.E.E.D.O.M. method - a holistic approach covering **F**aith • **R**ejuvenation • **E**motional Balance • **E**lectric Health • **D**etoxification • **O**xygenation • **M**indfulness.\n\nWhat brings you here today? I'd love to learn about your health goals and how I can help. 💚",
      timestamp: new Date().toLocaleTimeString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFreedomInfo, setShowFreedomInfo] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [savedRecommendations, setSavedRecommendations] = useState([]);
  const messagesEndRef = useRef(null);
  
  const FREE_MESSAGE_LIMIT = 7; // Allow 7 free messages before requiring login

  const freedomPillars = [
    { letter: 'F', name: 'Faith', icon: '✨', description: 'Trust in your body\'s innate ability to heal - however that resonates with you.' },
    { letter: 'R', name: 'Rejuvenation', icon: '🔬', description: 'Understand your baseline health through testing and intentional interventions.' },
    { letter: 'E', name: 'Emotional Balance', icon: '💚', description: 'Build resilience and emotional intelligence for lasting wellness.' },
    { letter: 'E', name: 'Electric Health', icon: '⚡', description: 'Work with your body\'s bioelectrical nature through frequencies and vibrations.' },
    { letter: 'D', name: 'Detoxification', icon: '🌿', description: 'Release what no longer serves you for optimal function.' },
    { letter: 'O', name: 'Oxygenation', icon: '💨', description: 'Increase cellular oxygenation through intentional practices.' },
    { letter: 'M', name: 'Mindfulness', icon: '🧠', description: 'Present-moment awareness and intentional focus on your wellness journey.' }
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Extract and save recommendations from Raphael's response
  const extractAndSaveRecommendations = async (responseText, userQuestion) => {
    if (!isAuthenticated || !token) return;
    
    // Simple extraction of product/service mentions
    const recommendations = [];
    
    // Check for product mentions
    const productPatterns = [
      /Thorne\s+[\w\s-]+/gi,
      /Apex Energetics\s+[\w\s-]+/gi,
      /Standard Process\s+[\w\s-]+/gi,
      /Function Health/gi,
      /magnesium\s*\w*/gi,
      /vitamin\s*[A-Z0-9]+/gi,
      /probiotic/gi,
      /omega-?\d*/gi
    ];
    
    productPatterns.forEach(pattern => {
      const matches = responseText.match(pattern);
      if (matches) {
        matches.forEach(match => {
          if (!recommendations.some(r => r.name.toLowerCase() === match.toLowerCase())) {
            recommendations.push({
              type: 'product',
              name: match.trim(),
              context: userQuestion.substring(0, 100)
            });
          }
        });
      }
    });
    
    // Check for coach recommendation
    if (responseText.toLowerCase().includes('coach directory') || 
        responseText.toLowerCase().includes('wellness coach') ||
        responseText.toLowerCase().includes('find a coach')) {
      recommendations.push({
        type: 'service',
        name: 'Hackster Wellness Coach',
        context: 'Personalized 1:1 guidance recommended'
      });
    }
    
    // Check for lab test recommendations
    if (responseText.toLowerCase().includes('function health') ||
        responseText.toLowerCase().includes('lab test') ||
        responseText.toLowerCase().includes('blood panel')) {
      recommendations.push({
        type: 'service',
        name: 'Lab Testing',
        context: 'Baseline health assessment recommended'
      });
    }
    
    // Save recommendations to backend
    if (recommendations.length > 0) {
      try {
        await axios.post(`${API}/coach/recommendations`, {
          recommendations: recommendations,
          conversation_context: userQuestion
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSavedRecommendations(prev => [...prev, ...recommendations]);
      } catch (error) {
        console.error('Error saving recommendations:', error);
      }
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    // Check if non-authenticated user has exceeded free messages
    if (!isAuthenticated && messageCount >= FREE_MESSAGE_LIMIT) {
      setShowLoginPrompt(true);
      return;
    }
    
    const userMessage = {
      id: messages.length + 1,
      sender: 'user',
      text: inputMessage,
      timestamp: new Date().toLocaleTimeString()
    };
    
    const currentQuestion = inputMessage;
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setMessageCount(prev => prev + 1);

    try {
      // Call backend AI endpoint
      const response = await axios.post(`${API}/coach/chat`, {
        message: currentQuestion,
        conversation_history: messages.slice(-10).map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text
        }))
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      const raphaelReply = {
        id: messages.length + 2,
        sender: 'raphael',
        text: response.data.response,
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, raphaelReply]);
      
      // Extract and save recommendations if user is logged in
      await extractAndSaveRecommendations(response.data.response, currentQuestion);
      
      // Show login prompt after receiving response if near limit
      if (!isAuthenticated && messageCount >= FREE_MESSAGE_LIMIT - 1) {
        setTimeout(() => setShowLoginPrompt(true), 2000);
      }
    } catch (error) {
      console.error('Chat error:', error);
      // Fallback response if API fails
      const fallbackReply = {
        id: messages.length + 2,
        sender: 'raphael',
        text: "🙏 Thank you for reaching out, dear friend. While I reflect on your question, let me remind you of the F.R.E.E.D.O.M. principles:\n\n**Faith** - Trust in your body's ability to heal.\n**Rejuvenation** - Understand your baseline through testing.\n**Emotional Balance** - Nurture your mental wellness.\n**Electric Health** - Embrace healing frequencies.\n**Detoxification** - Release what no longer serves you.\n**Oxygenation** - Breathe life into every cell.\n**Mindfulness** - Present-moment awareness on your journey.\n\nWhat aspect of your healing journey would you like to explore?",
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, fallbackReply]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickPrompts = [
    "How can I increase my energy naturally?",
    "What detox methods do you recommend?",
    "Help me with stress and emotional health",
    "Tell me about the F.R.E.E.D.O.M. method"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Session Status Banner */}
        {!isAuthenticated && (
          <div className="mb-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">💬</span>
              <div>
                <p className="font-medium text-gray-800">
                  Free Session: {FREE_MESSAGE_LIMIT - messageCount} messages remaining
                </p>
                <p className="text-sm text-gray-600">Sign in to save recommendations and continue unlimited conversations</p>
              </div>
            </div>
            <Link to="/signin" className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700">
              Sign In
            </Link>
          </div>
        )}
        
        {/* Logged In - Recommendations Saved Indicator */}
        {isAuthenticated && savedRecommendations.length > 0 && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✅</span>
              <div>
                <p className="font-medium text-green-800">
                  {savedRecommendations.length} recommendation(s) saved to your dashboard
                </p>
                <p className="text-sm text-green-600">View them anytime in your personal dashboard</p>
              </div>
            </div>
            <Link to="/dashboard" className="text-green-700 hover:text-green-800 text-sm font-medium underline">
              View Dashboard →
            </Link>
          </div>
        )}

        {/* F.R.E.E.D.O.M. Info Toggle */}
        <div className="mb-6">
          <button
            onClick={() => setShowFreedomInfo(!showFreedomInfo)}
            className="w-full bg-white rounded-xl shadow-md p-4 flex items-center justify-between hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">✨</span>
              <div className="text-left">
                <h3 className="font-bold text-gray-900">The F.R.E.E.D.O.M. Healing Method</h3>
                <p className="text-sm text-gray-600">Discover the 7 pillars of holistic wellness</p>
              </div>
            </div>
            <span className="text-gray-400">{showFreedomInfo ? '▲' : '▼'}</span>
          </button>
          
          {showFreedomInfo && (
            <div className="mt-4 bg-white rounded-xl shadow-md p-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {freedomPillars.map((pillar, index) => (
                  <div key={index} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 text-center">
                    <div className="text-3xl mb-2">{pillar.icon}</div>
                    <div className="font-bold text-purple-600">{pillar.letter} - {pillar.name}</div>
                    <p className="text-xs text-gray-600 mt-1">{pillar.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-2xl shadow-xl flex flex-col" style={{ height: '600px' }}>
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <span className="text-3xl">👼</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">Raphael - Your Wellness Guide</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-sm text-purple-200">F.R.E.E.D.O.M. Healing Coach</span>
                  </div>
                </div>
              </div>
              {isAuthenticated && (
                <div className="text-right text-sm">
                  <p className="text-purple-200">Logged in as</p>
                  <p className="font-medium">{user?.username}</p>
                </div>
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            {messages.map(message => (
              <div key={message.id} className={`mb-4 ${message.sender === 'user' ? 'text-right' : 'text-left'}`}>
                <div className={`inline-block max-w-md p-4 rounded-2xl ${
                  message.sender === 'user' 
                    ? 'bg-blue-600 text-white rounded-br-md' 
                    : 'bg-white text-gray-900 shadow-md rounded-bl-md'
                }`}>
                  {message.sender === 'raphael' && (
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                      <span className="text-lg">👼</span>
                      <span className="font-semibold text-purple-600">Raphael</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                  <p className="text-xs mt-2 opacity-60">{message.timestamp}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="text-left mb-4">
                <div className="inline-block bg-white p-4 rounded-2xl shadow-md">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">👼</span>
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-sm text-gray-500">Raphael is reflecting...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompts */}
          <div className="px-4 py-2 border-t bg-white">
            <div className="flex flex-wrap gap-2">
              {quickPrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => setInputMessage(prompt)}
                  disabled={!isAuthenticated && messageCount >= FREE_MESSAGE_LIMIT}
                  className="text-xs bg-purple-50 text-purple-700 px-3 py-1.5 rounded-full hover:bg-purple-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-4 border-t bg-white rounded-b-2xl">
            <div className="flex space-x-3">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={!isAuthenticated && messageCount >= FREE_MESSAGE_LIMIT 
                  ? "Sign in to continue chatting with Raphael..." 
                  : "Ask Raphael about your wellness journey..."}
                className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100"
                disabled={isLoading || (!isAuthenticated && messageCount >= FREE_MESSAGE_LIMIT)}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputMessage.trim() || (!isAuthenticated && messageCount >= FREE_MESSAGE_LIMIT)}
                className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isLoading ? '...' : 'Send'}
              </button>
            </div>
          </div>
        </div>

        {/* Login Prompt Modal */}
        {showLoginPrompt && !isAuthenticated && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
              <div className="text-6xl mb-4">👼</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Continue Your Healing Journey</h2>
              <p className="text-gray-600 mb-6">
                Sign in to unlock unlimited conversations with Raphael and save personalized product & service recommendations to your dashboard.
              </p>
              
              <div className="bg-purple-50 rounded-xl p-4 mb-6 text-left">
                <h4 className="font-semibold text-purple-800 mb-2">✨ Benefits of signing in:</h4>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• Unlimited conversations with Raphael</li>
                  <li>• Save product recommendations to your Stack</li>
                  <li>• Track your wellness journey progress</li>
                  <li>• Get personalized coach recommendations</li>
                  <li>• Access your dashboard anytime</li>
                </ul>
              </div>
              
              <div className="flex flex-col gap-3">
                <Link 
                  to="/signup/member" 
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-xl font-semibold hover:opacity-90"
                >
                  Create Free Account
                </Link>
                <Link 
                  to="/signin" 
                  className="w-full border-2 border-purple-600 text-purple-600 py-3 rounded-xl font-semibold hover:bg-purple-50"
                >
                  Sign In
                </Link>
                <button 
                  onClick={() => setShowLoginPrompt(false)}
                  className="text-gray-500 hover:text-gray-700 text-sm"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <p className="text-center text-xs text-gray-500 mt-4">
          Raphael provides wellness guidance based on the F.R.E.E.D.O.M. method. 
          Always consult healthcare professionals for medical advice.
        </p>
      </div>
      <Footer />
    </div>
  );
};

// ============== MARKETPLACE PAGE ==============
const MarketplacePage = () => {
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [showCart, setShowCart] = useState(false);
  const { isAuthenticated, token } = useAuth();

  const categories = [
    { value: 'all', label: 'All Products' },
    { value: 'supplements', label: 'Supplements' },
    { value: 'vitamins', label: 'Vitamins' },
    { value: 'minerals', label: 'Minerals' },
    { value: 'adaptogens', label: 'Adaptogens' },
    { value: 'amino_acids', label: 'Amino Acids' },
    { value: 'devices', label: 'Devices' }
  ];

  useEffect(() => {
    fetchProducts();
    fetchVendors();
    if (isAuthenticated) {
      fetchCart();
    }
  }, [selectedCategory, selectedVendor, searchQuery, isAuthenticated]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      let url = `${API}/products?limit=50`;
      if (selectedCategory !== 'all') url += `&category=${selectedCategory}`;
      if (selectedVendor !== 'all') url += `&vendor_id=${selectedVendor}`;
      if (searchQuery) url += `&search=${searchQuery}`;
      
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`);
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchCart = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const addToCart = async (product) => {
    if (!isAuthenticated) {
      alert('Please sign in to add items to your cart');
      return;
    }
    try {
      const response = await axios.post(`${API}/cart/items`, 
        { product_id: product.id, quantity: 1 },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCart(response.data);
      setShowCart(true);
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding to cart. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Hackster Marketplace</h1>
          <p className="text-xl text-blue-100 max-w-2xl">
            Premium biohacking products from trusted vendors. Science-backed supplements, 
            cutting-edge devices, and everything you need to optimize your health.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters & Search */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vendor</label>
              <select
                value={selectedVendor}
                onChange={(e) => setSelectedVendor(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Vendors</option>
                {vendors.map(vendor => (
                  <option key={vendor.slug} value={vendor.slug}>{vendor.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setShowCart(true)}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center"
              >
                🛒 Cart ({cart.items?.length || 0})
              </button>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading products...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map(product => (
              <div key={product.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="h-48 bg-gray-100 flex items-center justify-center">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="h-full w-full object-contain p-4" />
                  ) : (
                    <span className="text-6xl">💊</span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">{product.vendor_name}</span>
                    <span className="text-xs text-gray-500">{product.category}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.short_description || product.description}</p>
                  
                  {/* Health Goals Tags */}
                  {product.health_goals && product.health_goals.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {product.health_goals.slice(0, 3).map(goal => (
                        <span key={goal} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                          {goal.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      {product.sale_price ? (
                        <div className="flex items-center space-x-2">
                          <span className="text-lg font-bold text-blue-600">${product.sale_price}</span>
                          <span className="text-sm text-gray-400 line-through">${product.price}</span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">${product.price}</span>
                      )}
                    </div>
                    <div className="flex items-center text-yellow-500 text-sm">
                      ⭐ {product.rating.toFixed(1)}
                    </div>
                  </div>

                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={() => addToCart(product)}
                      className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      Add to Cart
                    </button>
                    {product.affiliate_url && (
                      <a
                        href={product.affiliate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 text-sm"
                      >
                        View
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {products.length === 0 && !loading && (
          <div className="text-center py-12">
            <span className="text-6xl">🔍</span>
            <h3 className="text-xl font-bold text-gray-900 mt-4">No products found</h3>
            <p className="text-gray-600">Try adjusting your filters or search query</p>
          </div>
        )}
      </div>

      {/* Shopping Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowCart(false)}>
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="p-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Your Cart</h2>
                <button onClick={() => setShowCart(false)} className="text-gray-500 hover:text-gray-700">✕</button>
              </div>
              
              {cart.items?.length > 0 ? (
                <>
                  <div className="flex-1 overflow-y-auto">
                    {cart.items.map(item => (
                      <div key={item.id} className="flex items-center py-4 border-b">
                        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                          {item.image_url ? (
                            <img src={item.image_url} alt={item.product_name} className="h-full w-full object-contain" />
                          ) : (
                            <span className="text-2xl">💊</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{item.product_name}</h4>
                          <p className="text-sm text-gray-500">{item.vendor_name}</p>
                          <p className="text-blue-600 font-medium">${item.price} × {item.quantity}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg font-bold mb-4">
                      <span>Subtotal:</span>
                      <span className="text-blue-600">${cart.subtotal?.toFixed(2) || '0.00'}</span>
                    </div>
                    <button className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700">
                      Proceed to Checkout
                    </button>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Orders ship directly from vendor partners
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                  <span className="text-6xl mb-4">🛒</span>
                  <p>Your cart is empty</p>
                  <Link to="/marketplace" className="mt-4 text-blue-600 hover:underline" onClick={() => setShowCart(false)}>
                    Browse Products
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

// ============== AI QUESTIONNAIRE PAGE ==============
const AIQuestionnairePage = () => {
  const [questionnaire, setQuestionnaire] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [error, setError] = useState(null);
  const { isAuthenticated, token } = useAuth();
  const [addedStack, setAddedStack] = useState({});
  const [addedCart, setAddedCart] = useState({});
  const [busyItem, setBusyItem] = useState(null);
  const [needAuth, setNeedAuth] = useState(false);

  const authCfg = () => (token ? { headers: { Authorization: `Bearer ${token}` } } : null);
  const parsePrice = (p) => {
    if (typeof p === 'number') return p;
    const n = parseFloat(String(p || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : n;
  };
  const recPayload = (product) => ({
    product_id: product.product_id || product.slug || null,
    product_name: product.name,
    vendor_name: product.brand || product.vendor_name || '',
    price: parsePrice(product.price),
    category: product.category || null,
    notes: product.reason || null,
    priority: product.priority || 0,
  });

  const addRecToStack = async (product) => {
    if (!isAuthenticated) { setNeedAuth(true); return; }
    setBusyItem(`stack-${product.name}`);
    try {
      await axios.post(`${API}/me/stack/items`, recPayload(product), authCfg());
      setAddedStack(prev => ({ ...prev, [product.name]: true }));
    } catch (e) {
      console.error('add to stack error', e);
      alert('Could not add to your stack. Please try again.');
    } finally { setBusyItem(null); }
  };

  const addRecToCart = async (product) => {
    if (!isAuthenticated) { setNeedAuth(true); return; }
    setBusyItem(`cart-${product.name}`);
    try {
      await axios.post(`${API}/me/cart/items`, recPayload(product), authCfg());
      setAddedCart(prev => ({ ...prev, [product.name]: true }));
    } catch (e) {
      console.error('add to cart error', e);
      alert('Could not add to your cart. Please try again.');
    } finally { setBusyItem(null); }
  };

  const startNewAssessment = () => {
    localStorage.removeItem('hacksterLastRec');
    setRecommendations(null);
    setResponses({});
    setCurrentQuestion(0);
    setAddedStack({});
    setAddedCart({});
    setNeedAuth(false);
  };

  useEffect(() => {
    fetchQuestionnaire();
    try {
      const saved = localStorage.getItem('hacksterLastRec');
      if (saved) setRecommendations(JSON.parse(saved));
    } catch (e) { /* ignore */ }
  }, []);

  const fetchQuestionnaire = async () => {
    try {
      const response = await axios.get(`${API}/questionnaire`);
      setQuestionnaire(response.data);
    } catch (error) {
      console.error('Error fetching questionnaire:', error);
      setError('Failed to load questionnaire. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = (questionId, answer) => {
    setResponses(prev => ({ ...prev, [questionId]: answer }));
  };

  const submitQuestionnaire = async () => {
    try {
      setSubmitting(true);
      const formattedResponses = Object.entries(responses).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const response = await axios.post(`${API}/questionnaire/submit`, {
        responses: formattedResponses
      });
      
      setRecommendations(response.data);
      try { localStorage.setItem('hacksterLastRec', JSON.stringify(response.data)); } catch (e) { /* ignore */ }
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      setError('Failed to generate recommendations. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const questions = questionnaire?.questions || [];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const currentQ = questions[currentQuestion];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your personalized assessment...</p>
        </div>
      </div>
    );
  }

  if (recommendations) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">🎯</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Personalized Hackster Stack</h1>
            <p className="text-gray-600">AI-powered recommendations based on your health profile</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <span className="text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full">✓ Saved — revisit these anytime</span>
              <button onClick={startNewAssessment} className="text-sm text-blue-600 hover:text-blue-700 font-medium underline">Start a new assessment</button>
            </div>
          </div>

          {needAuth && !isAuthenticated && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-amber-800 text-sm">Create a free account to save items to your stack and cart — your recommendations stay right here.</p>
              <div className="flex gap-2 shrink-0">
                <Link to="/signin" className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700">Sign In</Link>
                <Link to="/get-started" className="border border-amber-600 text-amber-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-100">Create Account</Link>
              </div>
            </div>
          )}

          {/* Health Score */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg opacity-90">Your Health Score</h2>
                <p className="text-4xl font-bold">{recommendations.health_score}/100</p>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-90">Primary Goals</p>
                <div className="flex flex-wrap gap-2 justify-end mt-1">
                  {recommendations.primary_goals?.map(goal => (
                    <span key={goal} className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
                      {goal.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Personalized Summary */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">📋 Your Personalized Summary</h3>
            <p className="text-gray-700 leading-relaxed">{recommendations.personalized_summary}</p>
          </div>

          {/* Recommended Products */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">💊 Recommended Products</h3>
            <div className="space-y-4">
              {recommendations.recommended_products?.map((product, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-900">{product.name}</h4>
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{product.brand}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          product.priority === 1 ? 'bg-blue-100 text-blue-700' :
                          product.priority === 2 ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          Priority {product.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{product.reason}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button 
                      onClick={() => addRecToStack(product)}
                      disabled={addedStack[product.name] || busyItem === `stack-${product.name}`}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                        addedStack[product.name]
                          ? 'bg-green-100 text-green-700 cursor-default'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                    >
                      {addedStack[product.name]
                        ? <><span>✓</span> Added to Stack</>
                        : busyItem === `stack-${product.name}`
                          ? <>Adding…</>
                          : <><span>⚡</span> Add to Stack</>}
                    </button>
                    <button 
                      onClick={() => addRecToCart(product)}
                      disabled={addedCart[product.name] || busyItem === `cart-${product.name}`}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                        addedCart[product.name]
                          ? 'bg-green-100 text-green-700 cursor-default'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {addedCart[product.name]
                        ? <><span>✓</span> In Cart</>
                        : busyItem === `cart-${product.name}`
                          ? <>Adding…</>
                          : <><span>🛒</span> Add to Cart</>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-blue-800 text-sm">
                💡 <strong>Tip:</strong> Add products to your Hackster Stack to save them for later, 
                then share your stack with friends and family who can help you purchase items as gifts!
              </p>
            </div>
          </div>

          {/* Recommended Lab Tests */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">🔬 Recommended Lab Tests</h3>
            <div className="space-y-4">
              {recommendations.recommended_lab_tests?.map((test, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                  <h4 className="font-semibold text-gray-900">{test.name}</h4>
                  <p className="text-xs text-blue-600 mb-1">{test.provider}</p>
                  <p className="text-sm text-gray-600">{test.reason}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recommended Health Coaches */}
          {recommendations.recommended_coaches && recommendations.recommended_coaches.length > 0 && (
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">👩‍⚕️ Top Health Coaches For You</h3>
                <Link to="/coaches" className="text-sm text-blue-600 hover:text-blue-700 font-medium">View all →</Link>
              </div>
              <p className="text-sm text-gray-600 mb-5">Hand-picked Hackster coaches matched to your goals.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                {recommendations.recommended_coaches.map((coach, idx) => (
                  <div key={coach.id || idx} className="border border-gray-200 rounded-xl p-4 bg-gradient-to-b from-white to-blue-50/30 hover:shadow-lg transition-shadow flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                      {coach.profile_image ? (
                        <img src={coach.profile_image} alt={coach.name} className="w-14 h-14 rounded-full object-cover border-2 border-blue-100" />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                          {coach.name?.split(' ').map(n => n[0]).join('').slice(0,2)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 text-sm truncate">{coach.name}</h4>
                        <p className="text-xs text-gray-500 truncate">{coach.location}</p>
                        <div className="flex items-center gap-1 text-xs text-yellow-600 mt-0.5">
                          <span>★</span><span className="font-medium">{coach.rating?.toFixed?.(1) || coach.rating}</span>
                          <span className="text-gray-400">({coach.total_reviews})</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(coach.specialties || []).slice(0, 3).map(s => (
                        <span key={s} className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                          {s}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mb-3 line-clamp-3 flex-1">{coach.bio}</p>
                    <div className="text-xs text-gray-700 font-medium mb-3">{coach.hourly_rate}</div>
                    <Link
                      to="/coaches"
                      className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-3 py-2 rounded-lg"
                    >
                      View Profile
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Lifestyle Tips */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">🌟 Biohacking Tips</h3>
            <ul className="space-y-3">
              {recommendations.lifestyle_tips?.map((tip, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-500 mr-3 mt-1">✓</span>
                  <span className="text-gray-700">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Learn More — Education Library */}
          {recommendations.recommended_content && recommendations.recommended_content.length > 0 && (
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">📚 Learn More</h3>
                <Link to="/library" className="text-emerald-600 text-sm font-medium hover:underline">Browse the Library →</Link>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {recommendations.recommended_content.map((c, idx) => (
                  <Link key={idx} to="/library" className="block border border-gray-100 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                    {c.image_url && (
                      <div className="h-24 bg-gray-100 overflow-hidden">
                        <img src={c.image_url} alt={c.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-3">
                      <span className="text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">{(c.category || '').replace(/_/g, ' ')}</span>
                      <h4 className="font-semibold text-gray-900 text-sm mt-2 leading-snug">{c.title}</h4>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{c.summary}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* AI Reasoning (Expandable) */}
          <details className="bg-gray-100 rounded-xl p-6 mb-8">
            <summary className="cursor-pointer font-semibold text-gray-700">🤖 View AI Analysis Details</summary>
            <div className="mt-4 text-sm text-gray-600 whitespace-pre-wrap">
              {recommendations.ai_reasoning}
            </div>
          </details>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link 
              to="/my-stack" 
              className="flex-1 bg-purple-600 text-white px-6 py-4 rounded-xl font-semibold text-center hover:bg-purple-700"
            >
              Save to My Stack
            </Link>
            <Link 
              to="/dashboard" 
              className="flex-1 border-2 border-blue-600 text-blue-600 px-6 py-4 rounded-xl font-semibold text-center hover:bg-blue-50"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <Navigation />
      
      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Question {currentQuestion + 1} of {questions.length}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-6">
            <span className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full uppercase tracking-wide">
              {currentQ?.category}
            </span>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {currentQ?.question}
          </h2>

          {/* Answer Options */}
          <div className="space-y-3 mb-8">
            {currentQ?.question_type === 'single_choice' && currentQ?.options?.map(option => (
              <button
                key={option}
                onClick={() => handleResponse(currentQ.id, option)}
                className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                  responses[currentQ.id] === option
                    ? 'border-blue-500 bg-blue-50 text-blue-800'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                {option}
              </button>
            ))}

            {currentQ?.question_type === 'multiple_choice' && currentQ?.options?.map(option => {
              const selected = responses[currentQ.id] || [];
              const isSelected = selected.includes(option);
              return (
                <button
                  key={option}
                  onClick={() => {
                    const updated = isSelected 
                      ? selected.filter(o => o !== option)
                      : [...selected, option];
                    handleResponse(currentQ.id, updated);
                  }}
                  className={`w-full p-4 text-left rounded-xl border-2 transition-all flex items-center ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-800'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                    isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                  }`}>
                    {isSelected && <span className="text-white text-xs">✓</span>}
                  </div>
                  {option}
                </button>
              );
            })}

            {currentQ?.question_type === 'scale' && (
              <div className="py-4">
                <input
                  type="range"
                  min={currentQ.scale_min || 1}
                  max={currentQ.scale_max || 10}
                  value={responses[currentQ.id] ?? 5}
                  onChange={(e) => handleResponse(currentQ.id, parseInt(e.target.value))}
                  onInput={(e) => handleResponse(currentQ.id, parseInt(e.target.value))}
                  className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer"
                />
                <div className="flex justify-between mt-2 text-sm text-gray-600">
                  <span>{currentQ.scale_min || 1} (Low)</span>
                  <span className="text-2xl font-bold text-blue-600">{responses[currentQ.id] ?? 5}</span>
                  <span>{currentQ.scale_max || 10} (High)</span>
                </div>
              </div>
            )}

            {currentQ?.question_type === 'text' && (
              <textarea
                value={responses[currentQ.id] || ''}
                onChange={(e) => handleResponse(currentQ.id, e.target.value)}
                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                rows="4"
                placeholder="Enter your response..."
              />
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <button
              onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
              disabled={currentQuestion === 0}
              className="px-6 py-3 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>

            {currentQuestion < questions.length - 1 ? (
              <button
                onClick={() => setCurrentQuestion(prev => prev + 1)}
                disabled={currentQ?.question_type !== 'text' && currentQ?.question_type !== 'scale' && !responses[currentQ?.id]}
                className="px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            ) : (
              <button
                onClick={submitQuestionnaire}
                disabled={submitting || (currentQ?.question_type !== 'text' && currentQ?.question_type !== 'scale' && !responses[currentQ?.id])}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    Generating...
                  </>
                ) : (
                  'Get My Recommendations 🚀'
                )}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg text-center">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== MEMBER DASHBOARD ==============
const MemberDashboard = () => {
  const { isAuthenticated, user, token } = useAuth();
  const [stacks, setStacks] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [raphaelRecommendations, setRaphaelRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchUserData();
    }
  }, [isAuthenticated, token]);

  const fetchUserData = async () => {
    try {
      // Fetch user's stacks
      const stacksRes = await axios.get(`${API}/stacks/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStacks(stacksRes.data);

      // Fetch user's AI recommendations
      const recsRes = await axios.get(`${API}/recommendations/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecommendations(recsRes.data);

      // Fetch Raphael's recommendations
      try {
        const raphaelRes = await axios.get(`${API}/coach/recommendations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRaphaelRecommendations(raphaelRes.data.recommendations || []);
      } catch (error) {
        console.error('Error fetching Raphael recommendations:', error);
        // Don't fail the whole dashboard if this fails
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="text-6xl mb-6">🔐</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Sign in to access your dashboard</h1>
          <p className="text-gray-600 mb-8">Track your progress, view recommendations, and manage your Hackster Stack</p>
          <Link to="/signin" className="bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl font-bold mb-2">Welcome back, {user?.username}! 👋</h1>
          <p className="text-blue-100 text-lg">Track your biohacking journey and optimize your health</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your dashboard...</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Quick Actions */}
            <div className="lg:col-span-2 space-y-6">
              {/* Stats Cards */}
              <div className="grid sm:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl shadow-md p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600">{stacks.length}</div>
                  <div className="text-gray-600">My Stacks</div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 text-center">
                  <div className="text-3xl font-bold text-purple-600">{recommendations.length}</div>
                  <div className="text-gray-600">AI Assessments</div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 text-center">
                  <div className="text-3xl font-bold text-green-600">{raphaelRecommendations.length}</div>
                  <div className="text-gray-600">Raphael Recommendations</div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 text-center">
                  <div className="text-3xl font-bold text-orange-600">{user?.posts_count || 0}</div>
                  <div className="text-gray-600">Community Posts</div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Link to="/questionnaire" className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl hover:from-blue-100 hover:to-purple-100 transition-colors">
                    <span className="text-3xl mr-4">🎯</span>
                    <div>
                      <div className="font-semibold text-gray-900">Get AI Recommendations</div>
                      <div className="text-sm text-gray-600">Take the health assessment</div>
                    </div>
                  </Link>
                  <Link to="/marketplace" className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl hover:from-blue-100 hover:to-purple-100 transition-colors">
                    <span className="text-3xl mr-4">🛒</span>
                    <div>
                      <div className="font-semibold text-gray-900">Shop Marketplace</div>
                      <div className="text-sm text-gray-600">Browse products</div>
                    </div>
                  </Link>
                  <Link to="/my-stack" className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl hover:from-blue-100 hover:to-purple-100 transition-colors">
                    <span className="text-3xl mr-4">⚡</span>
                    <div>
                      <div className="font-semibold text-gray-900">My Hackster Stack</div>
                      <div className="text-sm text-gray-600">Manage saved products</div>
                    </div>
                  </Link>
                  <Link to="/community" className="flex items-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl hover:from-blue-100 hover:to-purple-100 transition-colors">
                    <span className="text-3xl mr-4">💬</span>
                    <div>
                      <div className="font-semibold text-gray-900">Community</div>
                      <div className="text-sm text-gray-600">Share & connect</div>
                    </div>
                  </Link>
                </div>
              </div>

              {/* Recent Recommendations */}
              {recommendations.length > 0 && (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Your Latest AI Recommendations</h2>
                  <div className="space-y-4">
                    {recommendations.slice(0, 2).map((rec, index) => (
                      <div key={rec.id || index} className="border-l-4 border-blue-500 pl-4 py-2">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-900">Health Score: {rec.health_score}/100</span>
                          <span className="text-xs text-gray-500">{new Date(rec.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{rec.personalized_summary}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {rec.recommended_products?.slice(0, 3).map((product, idx) => (
                            <span key={idx} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                              {product.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link to="/questionnaire" className="mt-4 inline-block text-blue-600 hover:text-blue-700 text-sm font-medium">
                    Take New Assessment →
                  </Link>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Profile Card */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white text-3xl font-bold">{user?.username?.[0]?.toUpperCase() || 'H'}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{user?.username}</h3>
                  <p className="text-gray-600 text-sm">{user?.email}</p>
                  <div className="mt-3">
                    <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                      {user?.level || 'Member'}
                    </span>
                  </div>
                </div>
              </div>

              {/* My Stacks */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="font-bold text-gray-900 mb-4">My Stacks</h3>
                {stacks.length > 0 ? (
                  <div className="space-y-3">
                    {stacks.slice(0, 3).map(stack => (
                      <Link key={stack.id} to={`/my-stack`} className="block p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                        <div className="font-medium text-gray-900">{stack.name}</div>
                        <div className="text-sm text-gray-500">{stack.items?.length || 0} products</div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <p>No stacks yet</p>
                    <Link to="/my-stack" className="text-blue-600 hover:underline text-sm">Create your first stack</Link>
                  </div>
                )}
              </div>

              {/* Find a Coach */}
              <div className="bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl shadow-md p-6 text-white">
                <h3 className="font-bold mb-2">Need Expert Guidance?</h3>
                <p className="text-purple-100 text-sm mb-4">Connect with certified biohacking coaches for personalized support</p>
                <Link to="/coaches" className="inline-block bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-purple-50">
                  Find a Coach
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

// ============== MY STACK (WISHLIST) PAGE ==============
const MyStackPage = () => {
  const { isAuthenticated, token, user } = useAuth();
  const [stacks, setStacks] = useState([]);
  const [publicStacks, setPublicStacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddToStackModal, setShowAddToStackModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [selectedStack, setSelectedStack] = useState(null);
  const [checkoutStack, setCheckoutStack] = useState(null);
  const [newStack, setNewStack] = useState({ name: '', description: '', visibility: 'private', health_goals: [] });

  useEffect(() => {
    if (isAuthenticated) {
      fetchMyStacks();
    }
    fetchPublicStacks();
    
    // Check for pending product to add from questionnaire
    const pendingItem = localStorage.getItem('pendingStackItem');
    if (pendingItem) {
      try {
        const product = JSON.parse(pendingItem);
        setPendingProduct(product);
        setShowAddToStackModal(true);
        localStorage.removeItem('pendingStackItem');
      } catch (e) {
        console.error('Error parsing pending item:', e);
        localStorage.removeItem('pendingStackItem');
      }
    }
  }, [isAuthenticated]);

  const fetchMyStacks = async () => {
    try {
      const response = await axios.get(`${API}/stacks/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStacks(response.data);
    } catch (error) {
      console.error('Error fetching stacks:', error);
    }
  };

  const fetchPublicStacks = async () => {
    try {
      const response = await axios.get(`${API}/stacks?limit=20`);
      setPublicStacks(response.data);
    } catch (error) {
      console.error('Error fetching public stacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const addProductToStack = async (stackId, product) => {
    try {
      // First, we need to find or create a product in marketplace
      // For now, we'll add it as a custom item
      const response = await axios.post(`${API}/stacks/${stackId}/items`, 
        { 
          product_id: product.product_id || product.name.toLowerCase().replace(/\s+/g, '-'),
          priority: product.priority || 1,
          notes: product.reason || ''
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Refresh stacks
      await fetchMyStacks();
      setShowAddToStackModal(false);
      setPendingProduct(null);
      alert(`"${product.name}" has been added to your stack! 🎉`);
    } catch (error) {
      console.error('Error adding to stack:', error);
      // If product not found in marketplace, show message
      if (error.response?.status === 404) {
        alert(`Product "${product.name}" will be available in the marketplace soon. For now, you can find it at ${product.brand}'s website.`);
      } else {
        alert('Error adding product to stack. Please try again.');
      }
      setShowAddToStackModal(false);
    }
  };

  const createStackAndAddProduct = async () => {
    try {
      // Create a new stack first
      const stackData = {
        name: `My ${pendingProduct?.name || 'Health'} Stack`,
        description: `Stack created from AI recommendations`,
        visibility: 'private',
        health_goals: []
      };
      
      const response = await axios.post(`${API}/stacks`, stackData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newStackData = response.data;
      setStacks([newStackData, ...stacks]);
      
      // Now add the product to this new stack
      await addProductToStack(newStackData.id, pendingProduct);
    } catch (error) {
      console.error('Error creating stack:', error);
      alert('Error creating stack. Please try again.');
    }
  };

  const createStack = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/stacks`, newStack, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStacks([response.data, ...stacks]);
      setShowCreateModal(false);
      setNewStack({ name: '', description: '', visibility: 'private', health_goals: [] });
    } catch (error) {
      console.error('Error creating stack:', error);
      alert('Error creating stack. Please try again.');
    }
  };

  const healthGoalOptions = [
    'energy', 'sleep', 'focus', 'longevity', 'athletic_performance', 
    'weight_management', 'stress_management', 'immune_support', 'gut_health'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl font-bold mb-4">Hackster Stacks</h1>
          <p className="text-xl text-purple-100 max-w-2xl">
            Create and share your personalized biohacking product stacks. 
            Save your favorite products, get recommendations, and discover what others are using.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* My Stacks Section */}
        {isAuthenticated ? (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">My Stacks</h2>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
              >
                <span className="mr-2">+</span> Create New Stack
              </button>
            </div>

            {stacks.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {stacks.map(stack => (
                  <div key={stack.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-gray-900">{stack.name}</h3>
                      <span className={`text-xs px-2 py-1 rounded ${
                        stack.visibility === 'private' ? 'bg-gray-100 text-gray-600' :
                        stack.visibility === 'community' ? 'bg-blue-100 text-blue-600' :
                        'bg-purple-100 text-purple-600'
                      }`}>
                        {stack.visibility}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{stack.description || 'No description'}</p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{stack.items?.length || 0} products</span>
                      <span>${stack.total_value?.toFixed(2) || '0.00'} total</span>
                    </div>

                    {stack.health_goals?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {stack.health_goals.map(goal => (
                          <span key={goal} className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded">
                            {goal.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t flex items-center justify-between">
                      <div className="flex items-center space-x-3 text-sm text-gray-500">
                        <span>❤️ {stack.likes_count || 0}</span>
                        <span>💬 {stack.comments_count || 0}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {stack.items?.length > 0 && (
                          <button
                            onClick={() => setCheckoutStack(stack)}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold hover:opacity-90 flex items-center gap-1"
                          >
                            <span>🛒</span> Buy Stack
                          </button>
                        )}
                        <button
                          onClick={() => {
                            const shareUrl = `${window.location.origin}/stack/share/${stack.share_token}`;
                            navigator.clipboard.writeText(shareUrl);
                            alert('Share link copied! Send to friends & family so they can help you build your Hackster Stack 🎁');
                          }}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                        >
                          <span>🔗</span> Share
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <span className="text-6xl">📦</span>
                <h3 className="text-xl font-bold text-gray-900 mt-4">No stacks yet</h3>
                <p className="text-gray-600 mt-2">Create your first Hackster Stack to save products and share with the community</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700"
                >
                  Create Your First Stack
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-12 bg-gradient-to-r from-purple-100 to-blue-100 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Sign in to create your stack</h2>
            <p className="text-gray-600 mb-6">Save your favorite products, track your biohacking journey, and share with the community</p>
            <Link to="/signin" className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700">
              Sign In
            </Link>
          </div>
        )}

        {/* Community Stacks Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Community Stacks</h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : publicStacks.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publicStacks.map(stack => (
                <div key={stack.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                      {stack.username?.[0]?.toUpperCase() || 'H'}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{stack.name}</h3>
                      <p className="text-xs text-gray-500">by {stack.username}</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{stack.description || 'A curated biohacking stack'}</p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>{stack.items?.length || 0} products</span>
                    <span>${stack.total_value?.toFixed(2) || '0.00'}</span>
                  </div>

                  {stack.health_goals?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {stack.health_goals.slice(0, 3).map(goal => (
                        <span key={goal} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                          {goal.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center space-x-3 text-sm text-gray-500">
                      <span>❤️ {stack.likes_count || 0}</span>
                      <span>💬 {stack.comments_count || 0}</span>
                    </div>
                    <Link 
                      to={`/stack/${stack.id}`}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      View →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <span className="text-4xl">🌱</span>
              <p className="mt-4">No community stacks yet. Be the first to share!</p>
            </div>
          )}
        </div>
      </div>

      {/* One-click Stack Checkout Modal */}
      {checkoutStack && (
        <StackCheckout
          items={(checkoutStack.items || []).map(it => ({
            product_id: it.product_id,
            product_name: it.product_name,
            vendor_name: it.vendor_name,
            price: it.price,
            image_url: it.image_url,
          }))}
          user={user}
          source="stack"
          onClose={() => setCheckoutStack(null)}
        />
      )}

      {/* Create Stack Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Create New Stack</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <form onSubmit={createStack} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Stack Name</label>
                <input
                  type="text"
                  required
                  value={newStack.name}
                  onChange={(e) => setNewStack({...newStack, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., My Energy Stack"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newStack.description}
                  onChange={(e) => setNewStack({...newStack, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  rows="3"
                  placeholder="What's this stack for?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                <select
                  value={newStack.visibility}
                  onChange={(e) => setNewStack({...newStack, visibility: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="private">Private - Only you can see</option>
                  <option value="friends">Friends - Share with link</option>
                  <option value="community">Community - Visible on Hackster</option>
                  <option value="public">Public - Visible to everyone</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Health Goals</label>
                <div className="flex flex-wrap gap-2">
                  {healthGoalOptions.map(goal => (
                    <button
                      key={goal}
                      type="button"
                      onClick={() => {
                        const current = newStack.health_goals || [];
                        const updated = current.includes(goal)
                          ? current.filter(g => g !== goal)
                          : [...current, goal];
                        setNewStack({...newStack, health_goals: updated});
                      }}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        (newStack.health_goals || []).includes(goal)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {goal.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                >
                  Create Stack
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add to Stack Modal (from Questionnaire) */}
      {showAddToStackModal && pendingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Add to Stack</h2>
              <button 
                onClick={() => {
                  setShowAddToStackModal(false);
                  setPendingProduct(null);
                }} 
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Product Preview */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow">
                  <span className="text-2xl">💊</span>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{pendingProduct.name}</h3>
                  <p className="text-sm text-purple-600">{pendingProduct.brand}</p>
                </div>
              </div>
              {pendingProduct.reason && (
                <p className="text-sm text-gray-600 mt-2">{pendingProduct.reason}</p>
              )}
            </div>

            {stacks.length > 0 ? (
              <>
                <p className="text-gray-600 mb-4">Choose a stack to add this product:</p>
                <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                  {stacks.map(stack => (
                    <button
                      key={stack.id}
                      onClick={() => addProductToStack(stack.id, pendingProduct)}
                      className="w-full p-4 text-left rounded-xl border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{stack.name}</h4>
                          <p className="text-sm text-gray-500">{stack.items?.length || 0} products</p>
                        </div>
                        <span className="text-purple-600">→</span>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="border-t pt-4">
                  <button
                    onClick={createStackAndAddProduct}
                    className="w-full py-3 border-2 border-dashed border-purple-300 text-purple-600 rounded-xl hover:bg-purple-50 font-medium"
                  >
                    + Create New Stack & Add Product
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-600 mb-4">You don't have any stacks yet. Create one to save this product!</p>
                <button
                  onClick={createStackAndAddProduct}
                  className="bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-purple-700"
                >
                  Create My First Stack
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

// ============== SHARED STACK PAGE (Gift Registry View) ==============
const SharedStackPage = () => {
  const { shareToken } = useParams();
  const [stack, setStack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasingItem, setPurchasingItem] = useState(null);

  useEffect(() => {
    fetchSharedStack();
  }, [shareToken]);

  const fetchSharedStack = async () => {
    try {
      const response = await axios.get(`${API}/stacks/share/${shareToken}`);
      setStack(response.data);
    } catch (error) {
      console.error('Error fetching shared stack:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsPurchased = async (itemId) => {
    const purchaserName = prompt('Enter your name (so they know who bought this gift):');
    if (!purchaserName) return;
    
    try {
      await axios.put(`${API}/stacks/share/${shareToken}/items/${itemId}/purchase`, null, {
        params: { purchaser_name: purchaserName }
      });
      // Refresh the stack
      fetchSharedStack();
      alert('Thank you! This item has been marked as purchased. 🎁');
    } catch (error) {
      console.error('Error marking item as purchased:', error);
      alert('Error updating item. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!stack) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <div className="text-6xl mb-6">🔍</div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Stack not found</h1>
          <p className="text-gray-600">This Hackster Stack may have been removed or the link is invalid.</p>
        </div>
      </div>
    );
  }

  const unpurchasedItems = stack.items?.filter(item => !item.is_purchased) || [];
  const purchasedItems = stack.items?.filter(item => item.is_purchased) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <Navigation />
      
      {/* Hero */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-5xl mb-4">🎁</div>
          <h1 className="text-4xl font-bold mb-2">{stack.username}'s Hackster Stack</h1>
          <p className="text-purple-100 text-lg">{stack.name}</p>
          {stack.description && (
            <p className="text-purple-200 mt-2 max-w-xl mx-auto">{stack.description}</p>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Gift Registry Info */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="text-4xl">💝</div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Help {stack.username} Optimize Their Health!</h2>
              <p className="text-gray-600">
                {stack.username} is on a biohacking journey to improve their health and wellness. 
                You can help by purchasing items from their wishlist below. Click "I'll Buy This" 
                to mark an item as purchased and prevent duplicates.
              </p>
            </div>
          </div>
        </div>

        {/* Health Goals */}
        {stack.health_goals?.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-600 mb-2">Health Goals:</h3>
            <div className="flex flex-wrap gap-2">
              {stack.health_goals.map(goal => (
                <span key={goal} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">
                  {goal.replace('_', ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Items Still Needed */}
        {unpurchasedItems.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              🛒 Items Still Needed ({unpurchasedItems.length})
            </h2>
            <div className="space-y-4">
              {unpurchasedItems.map(item => (
                <div key={item.id} className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.product_name} className="w-full h-full object-contain rounded-lg" />
                        ) : (
                          <span className="text-2xl">💊</span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900">{item.product_name}</h3>
                        <p className="text-sm text-gray-500">{item.vendor_name}</p>
                        {item.notes && (
                          <p className="text-sm text-gray-600 mt-1 italic">"{item.notes}"</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-purple-600">${item.price?.toFixed(2)}</p>
                      {item.priority === 1 && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">High Priority</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => markAsPurchased(item.id)}
                      className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 flex items-center justify-center gap-2"
                    >
                      <span>🎁</span> I'll Buy This Gift
                    </button>
                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent(item.product_name + ' ' + item.vendor_name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50"
                    >
                      Find Product
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Already Purchased */}
        {purchasedItems.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              ✅ Already Purchased ({purchasedItems.length})
            </h2>
            <div className="space-y-3">
              {purchasedItems.map(item => (
                <div key={item.id} className="bg-gray-100 rounded-xl p-4 opacity-75">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-green-500 text-xl">✓</span>
                      <div>
                        <h3 className="font-medium text-gray-700 line-through">{item.product_name}</h3>
                        <p className="text-sm text-gray-500">Purchased by {item.purchased_by || 'Someone special'}</p>
                      </div>
                    </div>
                    <span className="text-gray-500">${item.price?.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="bg-gradient-to-r from-purple-100 to-blue-100 rounded-xl p-6 text-center">
          <p className="text-gray-700">
            <strong>{unpurchasedItems.length}</strong> items still needed • 
            <strong> ${unpurchasedItems.reduce((sum, item) => sum + (item.price || 0), 0).toFixed(2)}</strong> total remaining
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Thank you for supporting {stack.username}'s health journey! 💜
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

// Placeholder Pages
const PrivacyPolicy = () => (
  <div className="min-h-screen bg-gray-50">
    <Navigation />
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
      <div className="bg-white rounded-xl p-8 shadow-lg">
        <p className="text-gray-600 mb-4">Last updated: December 2024</p>
        <div className="space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
            <p className="text-gray-700">Hackster.ai is committed to protecting your privacy. This policy outlines how we collect, use, and protect your personal information.</p>
          </section>
          <div className="bg-blue-50 p-6 rounded-lg">
            <p className="text-blue-800">🚧 Full privacy policy coming soon. During beta, we follow industry-standard privacy practices.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TermsOfService = () => (
  <div className="min-h-screen bg-gray-50">
    <Navigation />
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
      <div className="bg-white rounded-xl p-8 shadow-lg">
        <p className="text-gray-600 mb-4">Last updated: December 2024</p>
        <div className="space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptance of Terms</h2>
            <p className="text-gray-700">By using Hackster.ai, you agree to these terms of service and our privacy policy.</p>
          </section>
          <div className="bg-blue-50 p-6 rounded-lg">
            <p className="text-blue-800">🚧 Full terms of service coming soon. During beta, standard platform usage terms apply.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Public Post View Component (for external sharing)
const PublicPostView = () => {
  const { postId } = useParams();
  const { isAuthenticated, user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);

  useEffect(() => {
    fetchPublicPost();
    fetchPublicComments();
  }, [postId]);

  const fetchPublicPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/public/${postId}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicComments = async () => {
    try {
      const response = await axios.get(`${API}/posts/public/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleEngagementClick = () => {
    if (!isAuthenticated) {
      setShowSignupPrompt(true);
    }
    // If authenticated, handle the actual engagement
  };

  const sharePost = () => {
    const postUrl = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: `Check out this biohacking post on Hackster.ai: ${post.title}`,
        url: postUrl,
      });
    } else {
      navigator.clipboard.writeText(postUrl);
      alert('Post link copied to clipboard!');
    }
  };

  const getUserLevel = (level) => {
    switch(level) {
      case 'hackster_pro': return { icon: '🟡', text: 'Hackster Pro', color: 'text-yellow-600 bg-yellow-100' };
      case 'contributor': return { icon: '🔵', text: 'Contributor', color: 'text-blue-600 bg-blue-100' };
      case 'member': return { icon: '🟢', text: 'Member', color: 'text-green-600 bg-green-100' };
      default: return { icon: '🟢', text: 'Member', color: 'text-green-600 bg-green-100' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Post Not Found</h2>
          <p className="text-gray-600 mb-6">The biohacking post you're looking for doesn't exist.</p>
          <Link to="/" className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700">
            Explore Hackster.ai
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Post Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-lg">{post.username.charAt(0).toUpperCase()}</span>
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900">{post.username}</h3>
                  {(() => {
                    const levelInfo = getUserLevel(post.user_level || 'member');
                    return (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${levelInfo.color} flex items-center space-x-1`}>
                        <span>{levelInfo.icon}</span>
                        <span>{levelInfo.text}</span>
                      </span>
                    );
                  })()}
                </div>
                <p className="text-sm text-gray-500">{new Date(post.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                post.category === 'supplements' ? 'bg-purple-100 text-purple-700' :
                post.category === 'recovery' ? 'bg-blue-100 text-blue-700' :
                post.category === 'sleep' ? 'bg-indigo-100 text-indigo-700' :
                post.category === 'nutrition' ? 'bg-green-100 text-green-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {post.category.charAt(0).toUpperCase() + post.category.slice(1)}
              </span>
              <button
                onClick={sharePost}
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <span>🔗</span>
                <span className="text-sm">Share</span>
              </button>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-6">{post.title}</h1>
          <div className="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap mb-8">{post.content}</div>

          {/* Engagement Section */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <button
                  onClick={handleEngagementClick}
                  className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors"
                >
                  <span className="text-xl">👍</span>
                  <span className="font-medium">{post.upvotes}</span>
                  <span className="text-sm">Upvotes</span>
                </button>
                <button
                  onClick={handleEngagementClick}
                  className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
                >
                  <span className="text-xl">👎</span>
                  <span className="font-medium">{post.downvotes}</span>
                </button>
                <div className="flex items-center space-x-2 text-gray-600">
                  <span className="text-xl">💬</span>
                  <span className="font-medium">{comments.length}</span>
                  <span className="text-sm">Comments</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {Object.entries(post.reaction_counts || {}).map(([reaction, count]) => (
                  <button 
                    key={reaction}
                    onClick={handleEngagementClick}
                    className="flex items-center space-x-1 text-sm text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <span>{
                      reaction === 'tried_this' ? '✅' :
                      reaction === 'helpful' ? '🔥' :
                      reaction === 'results' ? '📊' :
                      reaction === 'on_point' ? '🎯' : '👍'
                    }</span>
                    <span>{count}</span>
                    <span className="text-xs capitalize">{reaction.replace('_', ' ')}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        {comments.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Community Discussion ({comments.length})</h3>
            <div className="space-y-6">
              {comments.map((comment) => (
                <div key={comment.id} className="border-l-4 border-blue-200 pl-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-xs">{comment.username.charAt(0).toUpperCase()}</span>
                    </div>
                    <span className="font-semibold text-gray-900">{comment.username}</span>
                    <span className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-gray-700">{comment.content}</p>
                </div>
              ))}
            </div>
            
            {!isAuthenticated && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-blue-800 mb-3">Want to join the discussion?</p>
                <Link
                  to="/login"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Sign Up to Comment
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Join Community CTA for non-authenticated users */}
        {!isAuthenticated && (
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Love this biohacking content?</h2>
            <p className="text-blue-100 mb-6 text-lg">
              Join thousands of biohackers sharing their experiments, results, and insights on Hackster.ai
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup/member"
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Join the Community
              </Link>
              <Link
                to="/community"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                Explore More Posts
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Signup Prompt Modal */}
      {showSignupPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="text-center">
              <div className="text-4xl mb-4">🚀</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Join the Biohacking Community!</h3>
              <p className="text-gray-600 mb-6">
                Sign up to upvote, comment, and share your own biohacking experiments with fellow optimizers.
              </p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/signup/member"
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Create Free Account
                </Link>
                <button
                  onClick={() => setShowSignupPrompt(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Continue Browsing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-2.5 mb-4">
              <LogoMark size={40} dark />
              <span className="text-xl font-extrabold tracking-tight text-white">HACKSTER<span className="text-blue-400">.ai</span></span>
            </div>
            <p className="text-gray-400 text-sm">
              Your biohacking buddy for optimal health and performance.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Platform</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/questionnaire" className="hover:text-white">Get Started</Link></li>
              <li><Link to="/marketplace" className="hover:text-white">Hackster Store</Link></li>
              <li><Link to="/my-stack" className="hover:text-white">My Stack</Link></li>
              <li><Link to="/dashboard" className="hover:text-white">Dashboard</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Community</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/community" className="hover:text-white">Forum</Link></li>
              <li><Link to="/library" className="hover:text-white">Library</Link></li>
              <li><Link to="/chat" className="hover:text-white">AI Coach</Link></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="#" className="hover:text-white">Help Center</a></li>
              <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        
        {/* Medical Disclaimer */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-300 leading-relaxed">
              <strong className="text-yellow-500">Medical Disclaimer:</strong> The information provided by Hackster.ai, including AI recommendations, community discussions, and AI coach guidance, is for educational, research and informational purposes only. Always consult with qualified healthcare providers before making any changes to your health regimen, starting new supplements, or implementing biohacking protocols. Individual results may vary.
            </p>
            <p className="text-xs text-gray-400 mt-3">
              * Any recommendations on Hackster.ai have not been reviewed or approved by the Federal Drug Administration or FDA and are not intended to diagnose, treat, cure or prevent disease.
            </p>
          </div>
          <p className="text-center text-sm text-gray-400">&copy; 2026 Hackster.ai is a PLZ Company brand. All rights reserved. Beta Version.</p>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/library" element={<LibraryPage NavigationComponent={Navigation} />} />
            <Route path="/community" element={<CommunityLanding />} />
            <Route path="/community-forum" element={<CommunityPlatform />} />
            <Route path="/posts/:postId" element={<PublicPostView />} />
            <Route path="/get-started" element={<GetStartedFlow />} />
            <Route path="/login" element={<AuthSelectionPage />} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup/member" element={<MemberSignUpPage />} />
            <Route path="/signup/coach" element={<CoachSignUpPage />} />
            <Route path="/onboarding/coach" element={<CoachOnboardingPage />} />
            <Route path="/coach/dashboard" element={<CoachDashboardPage />} />
            <Route path="/coach/profile/edit" element={<CoachProfileEditPage />} />
            <Route path="/coaches" element={<CoachesPage />} />
            <Route path="/coaching" element={<CoachingLandingPage />} />
            
            {/* AI Coach Chat - Raphael */}
            <Route path="/chat" element={<AICoachChat />} />
            <Route path="/coach/raphael" element={<AICoachChat />} />
            
            {/* Marketplace & AI Features */}
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/questionnaire" element={<AIQuestionnairePage />} />
            <Route path="/my-stack" element={<MyStackPage />} />
            <Route path="/stack/:stackId" element={<MyStackPage />} />
            <Route path="/stack/share/:shareToken" element={<SharedStackPage />} />
            <Route path="/dashboard" element={<MemberDashboard />} />
            
            {/* Blog System (Phase 2) */}
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/experiments" element={<BlogPage />} />
            
            {/* Legal Pages */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;